/* eslint-disable no-console */
import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import toast from "react-hot-toast";
import { Loader, Header } from "../../components";
import { generarReciboPDF } from "../../components/ReciboSueldoPDF";
import { puedeCargarLiquidaciones } from "../../constants/permisos";
import { useAuthContext } from "../../context/auth";
import {
  getEmpleados,
  crearEmpleado,
  actualizarEmpleado,
  getVacacionesEmpleado,
  getVacacionesPais,
  agregarVacaciones,
  getRemuneracionesEmpleado,
  crearRemuneracion,
  actualizarRemuneracion,
} from "../../utils";
import { calcularLiquidacionChile } from "../../utils";
import { autoMaterializarComisionesRRHH } from "../../utils/autoMaterializarComisiones";
import {
  getUFValue,
  getUTMValue,
  isLoaded,
  isUTMLoaded,
  loadExchangeRates,
  loadUTM,
} from "../../utils/exchangeRateService";
import { fechaPagoEstimada } from "../../utils/fechaPagoSueldo";
import { resolverVentasDeComision } from "../../utils/liquidacionesContadora/detalleComisionRecibo";
import { extraerTextoLiquidacionPdf } from "../../utils/liquidacionesContadora/extraerTextoLiquidacionPdf";
import {
  getLiquidacionesContadoraEmpleado,
  getLiquidacionesContadoraPorEmpleados,
  getUrlLiquidacionPdf,
  subirLiquidacionPdf,
  guardarLiquidacionContadora,
} from "../../utils/liquidacionesContadora/liquidacionesContadoraApi";
import { parsearLiquidacionTexto } from "../../utils/liquidacionesContadora/parsearLiquidacionTexto";
import {
  getPagosEmpleados,
  getHaberesPais,
  getFechasPagoPais,
  crearHaberEmpleado,
  eliminarHaberEmpleado,
  sumarPagadoLiquido,
  saldoLiquidoPorPeriodo,
  pagosDelMesTransferido,
  sumarPagadoRecibo,
  pagosSinMovimientoBancario,
} from "../../utils/pagosEmpleados";
import { remuneracionVigenteParaMes } from "../../utils/remuneracionVigente";
import { useCurrency } from "../../utils/useCurrency";
// Estilo de la tabla del recibo de Liquidaciones — MISMO SCSS que VentasDetalle (10.161: cero
// estilos inventados). Import global (no CSS Module): las reglas quedan scopeadas por la clase
// literal "container-ventas-detalle" que envuelve la sección del recibo más abajo, igual que en
// VentasDetalle/index.js — nunca se copian ni modifican sus reglas, se reusa el archivo tal cual.
import "../../components/VentasDetalle/stylesVentasDetalle.scss";
import styles from "./stylesEmpleados.module.scss";

// Pestañas de Gestión de Empleados (Fase 1, 2026-10-01) — fuente única del orden/label,
// mismo patrón que VENTAS_TABS (docs/modulos/ventas-hub.md).
const TABS_EMPLEADOS = [
  { key: "equipo", label: "Equipo" },
  { key: "liquidaciones", label: "Liquidaciones" },
  { key: "haberes", label: "Haberes" },
  { key: "vacaciones", label: "Vacaciones" },
  { key: "ficha", label: "Ficha" },
];

const MESES = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

// Estado inicial del formulario de remuneración (incluye previsionales). Reutilizado en
// el useState inicial, al abrir el modal y tras guardar. Cap AFP arranca en 10% (norma).
const REM_FORM_VACIO = {
  inicio_vigencia: "",
  fin_vigencia: "",
  sueldo_base: "",
  comision_porcentaje: "",
  bonos_fijo: "",
  bonos_variable: "",
  afp_nombre: "",
  afp_cap_individual_pct: "10",
  afp_comision_pct: "",
  salud_tipo: "",
  salud_isapre_uf: "",
};

const GestionEmpleados = () => {
  const { pais, id: usuarioActualId } = useAuthContext();
  const { formatAmount } = useCurrency();
  // Liquidaciones de la contadora (PASO C2): admin = LIQUIDACIONES_ADMIN_IDS, ve/carga la de
  // cualquiera; el resto ve SOLO la suya (gate antes de cargar datos — 10.279; la base lo fuerza
  // igual vía RLS/la RPC, esto es además el gate de UI para no ni ofrecer el menú).
  const esAdminLiquidaciones = puedeCargarLiquidaciones(usuarioActualId);

  const [empleados, setEmpleados] = useState([]);
  const [loading, setLoading] = useState(true);
  // ─── Pestañas (Fase 1, 2026-10-01) ─────────────────────────────────────────
  // Reemplaza el molde modal-por-acción: Equipo/Liquidaciones/Haberes/Vacaciones/Ficha son
  // pestañas de página completa, barra en el header (mismo estilo que EstadoCuenta/Ventas,
  // nunca un componente nuevo). "Ficha" junta Datos + Remuneración (vigencias) + Egreso.
  const [activeTab, setActiveTab] = useState("equipo");
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState(null);
  const [vacaciones, setVacaciones] = useState([]); // del modal (1 empleado)
  const [vacacionesTodas, setVacacionesTodas] = useState([]); // para cards
  const [pagosEmpleados, setPagosEmpleados] = useState([]);
  // Haberes imponibles por-mes (bono one-off / comisión) — se pasan a calcularLiquidacionChile
  // como haberesMes. Cargados por país; se filtran por empleado/período en haberesDelMes.
  const [haberes, setHaberes] = useState([]);
  // Overrides de fecha de pago (pago_sueldo_fecha) — para la FECHA del bloque PRÓXIMO SUELDO.
  const [fechasPago, setFechasPago] = useState([]);
  const [showEgresados, setShowEgresados] = useState(false);
  const [remuneraciones, setRemuneraciones] = useState([]);
  const [remuneracionEditando, setRemuneracionEditando] = useState(null);
  // Campo de monto (sueldo/bonos) enfocado: mientras se edita se muestran dígitos crudos;
  // al salir del foco se re-formatea con separador de miles (formatMonto).
  const [montoFocus, setMontoFocus] = useState(null);
  const [formData, setFormData] = useState({
    nombre: "",
    apellido: "",
    fecha_nacimiento: "",
    inicio_contrato: "",
    fin_contrato: "",
    rut: "",
    cargo: "",
    telefono: "",
    email: "",
    devenga_vacaciones: true,
    pais,
  });
  const [vacacionForm, setVacacionForm] = useState({
    fecha_inicio: "",
    fecha_fin: "",
    notas: "",
  });
  const [egresoForm, setEgresoForm] = useState({
    egreso_fecha: "",
    egreso_monto: "",
  });
  const [remuneracionForm, setRemuneracionForm] = useState(REM_FORM_VACIO);
  // ─── Haberes (bono one-off / comisión del mes) ───
  const [haberMontoFocus, setHaberMontoFocus] = useState(false);
  const [haberPeriodo, setHaberPeriodo] = useState({
    mes: new Date().getMonth() + 1,
    ano: new Date().getFullYear(),
  });
  const [haberForm, setHaberForm] = useState({ tipo: "bono", monto: "", concepto: "", imponible: true });
  // ─── Modal de liquidación ─────────────────────────────────
  const [showLiquidacion, setShowLiquidacion] = useState(false);
  const [liquidacionEmpleado, setLiquidacionEmpleado] = useState(null);
  const [liquidacionMes, setLiquidacionMes] = useState(new Date().getMonth() + 1);
  const [liquidacionAno, setLiquidacionAno] = useState(new Date().getFullYear());
  // La columna "Líquido a pagar" usa calcularLiquidacionChile, que necesita la UF del mes
  // (tramo Isapre) y la UTM del mes (tramos del impuesto único). Sin las tasas cargadas
  // getUFValue/getUTMValue devuelven fallback. El flag fuerza el re-render cuando terminan
  // de cargar ambas (loadExchangeRates/loadUTM no tienen state).
  const [ratesListas, setRatesListas] = useState(isLoaded() && isUTMLoaded());
  // ─── Pestaña Liquidaciones de la contadora (PASO C2, rediseño estilo VentasDetalle) ───────
  const [liquidacionesEmpleado, setLiquidacionesEmpleado] = useState(null);
  const [liquidacionesLista, setLiquidacionesLista] = useState([]); // TODAS las versiones (vigente + históricas)
  const [liquidacionesCargandoLista, setLiquidacionesCargandoLista] = useState(false);
  const [liquidacionesAnoSel, setLiquidacionesAnoSel] = useState(new Date().getFullYear());
  const [liquidacionesMesSel, setLiquidacionesMesSel] = useState(new Date().getMonth() + 1);
  const [reciboAbierto, setReciboAbierto] = useState(false); // click en un mes → despliega el recibo debajo
  const [archivoLiquidacionSel, setArchivoLiquidacionSel] = useState(null); // File
  const [previewLiquidacion, setPreviewLiquidacion] = useState(null); // resultado de parsearLiquidacionTexto
  const [procesandoArchivoLiquidacion, setProcesandoArchivoLiquidacion] = useState(false);
  const [guardandoLiquidacionContadora, setGuardandoLiquidacionContadora] = useState(false);
  // Casilla "Analizar" (solo LIQUIDACIONES_ADMIN_IDS): suma columnas de comparación a la tabla anual.
  const [analizarLiquidaciones, setAnalizarLiquidaciones] = useState(false);
  // Detalle de comisión ("de qué ventas sale") del recibo abierto: toggle + caché por año-mes
  // (resolverVentasDeComision hace queries a sales/sales_contratos/clientes — no se repite si ya se
  // resolvió ese mes) + estado de carga dedicado (10.273).
  const [comisionAbierta, setComisionAbierta] = useState(false);
  const [detalleComisionCache, setDetalleComisionCache] = useState({});
  const [cargandoComision, setCargandoComision] = useState(false);
  // ─── Modal Carga masiva de liquidaciones (botón general, solo admin) ───────
  const [showCargaMasiva, setShowCargaMasiva] = useState(false);
  const [procesandoMasiva, setProcesandoMasiva] = useState(false);
  const [filasMasivas, setFilasMasivas] = useState([]); // [{archivo, file, resultado, estado, motivo, version}]
  const [guardandoMasiva, setGuardandoMasiva] = useState(false);
  const [progresoMasivo, setProgresoMasivo] = useState(null); // {hechos, total, archivo} mientras guarda
  const [resultadoMasivo, setResultadoMasivo] = useState(null); // {guardados, total, error} al terminar
  // Fuerza el remount del <input type="file"> tras guardar — limpia la selección nativa del picker
  // (setFilasMasivas([]) ya limpiaba la tabla de revisión, pero el input no controlado seguía
  // mostrando los nombres de los archivos ya cargados).
  const [cargaMasivaInputKey, setCargaMasivaInputKey] = useState(0);

  useEffect(() => {
    if (isLoaded() && isUTMLoaded()) return;
    Promise.all([loadExchangeRates(), loadUTM()])
      .then(() => setRatesListas(true))
      .catch((e) => console.error("Error cargando tasas:", e));
  }, []);

  // Auto-materialización de comisiones cobradas (Prod-1a): una vez por montaje (guard contra el doble
  // efecto de StrictMode y re-renders). Idempotente por (empleado, referencia) → cero doble-pago;
  // solo escribe cobros nuevos post-corte. Fire-and-forget: no bloquea la UI, sin ruido (solo debug).
  const autoMatRef = useRef(null);
  useEffect(() => {
    if (autoMatRef.current === pais) return; // ya corrió para este país (StrictMode/re-render safe)
    autoMatRef.current = pais;
    autoMaterializarComisionesRRHH(pais).catch((e) => console.error("[RRHH] auto-materialización:", e));
  }, [pais]);

  useEffect(() => {
    const fetchEmpleados = async () => {
      try {
        setLoading(true);
        const [empleadosData, pagosData, vacsData, haberesData, fechasPagoData] = await Promise.all([
          getEmpleados(pais),
          getPagosEmpleados({ pais }),
          getVacacionesPais(pais),
          getHaberesPais(pais),
          getFechasPagoPais(pais),
        ]);
        setEmpleados(empleadosData || []);
        setPagosEmpleados(pagosData || []);
        setVacacionesTodas(vacsData || []);
        setHaberes(haberesData || []);
        setFechasPago(fechasPagoData || []);
      } catch (error) {
        console.error("Error al cargar empleados:", error);
        alert("Error al cargar empleados");
      } finally {
        setLoading(false);
      }
    };
    fetchEmpleados();
  }, [pais]);

  useEffect(() => {
    if (empleados.length > 0) {
      const fetchAllRemuneraciones = async () => {
        try {
          const allRem = [];
          for (const emp of empleados) {
            const data = await getRemuneracionesEmpleado(emp.id);
            allRem.push(...(data || []));
          }
          setRemuneraciones(allRem);
        } catch (error) {
          console.error("Error al cargar todas las remuneraciones:", error);
        }
      };
      fetchAllRemuneraciones();
    }
  }, [empleados]);

  // Cálculo de vacaciones — usa vacacionesTodas para no depender del modal.
  // Considera fin_contrato como tope superior. 15 días/año Chile, 30 días/año Perú.
  const calcularVacacionesEmpleado = (empleado) => {
    if (!empleado.inicio_contrato) return { generados: 0, usados: 0, disponibles: 0 };
    // Flag de la ficha (4.1b): si no devenga, 0 días generados (aplica Chile y Perú).
    if (empleado.devenga_vacaciones === false) return { generados: 0, usados: 0, disponibles: 0 };
    const inicio = new Date(empleado.inicio_contrato);
    const hasta = empleado.fin_contrato ? new Date(empleado.fin_contrato) : new Date();
    const diasPorAno = empleado.pais === "Perú" ? 30 : 15;
    const anosTrabajados = (hasta - inicio) / (1000 * 60 * 60 * 24 * 365.25);
    const diasGenerados = Math.floor(anosTrabajados * diasPorAno);
    const diasUsados = (vacacionesTodas || [])
      .filter((v) => v.empleado_id === empleado.id)
      .reduce((total, v) => {
        const ini = new Date(v.fecha_inicio);
        const fin = new Date(v.fecha_fin);
        return total + Math.ceil((fin - ini) / (1000 * 60 * 60 * 24)) + 1;
      }, 0);
    return {
      generados: diasGenerados,
      usados: diasUsados,
      disponibles: Math.max(0, diasGenerados - diasUsados),
    };
  };

  // Wrapper para mantener compatibilidad con el modal de vacaciones (state singular).
  const calcularVacaciones = (empleado) => calcularVacacionesEmpleado(empleado);

  const formatFecha = (fechaStr) => {
    if (!fechaStr) return "-";
    return new Date(fechaStr + "T12:00:00").toLocaleDateString("es", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getPagosDelEmpleado = (empleadoId) => pagosEmpleados.filter((p) => p.empleado_id === empleadoId);

  // Los tres helpers que siguen ya no se consumen desde la tabla (la card los mostraba).
  // Se conservan a propósito: son la base de la ficha del empleado, que es la próxima fase.
  // eslint-disable-next-line no-unused-vars
  const getTotalPagado2026 = (empleadoId) =>
    pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.periodo_año === 2026)
      .reduce((sum, p) => sum + (Number(p.monto) || 0), 0);

  // eslint-disable-next-line no-unused-vars
  const getUltimoLiquido = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return sueldos[0]?.monto || null;
  };

  // eslint-disable-next-line no-unused-vars
  const getUltimoBono = (empleadoId) => {
    const bonos = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "bono")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return bonos[0] || null;
  };

  // eslint-disable-next-line no-unused-vars
  const getUltimaComision = (empleadoId) => {
    const coms = getPagosDelEmpleado(empleadoId)
      .filter((p) => p.tipo === "comision")
      .sort((a, b) => new Date(b.fecha_pago) - new Date(a.fecha_pago));
    return coms[0] || null;
  };

  // Remuneración efectiva del empleado para un mes PUNTUAL (regla 10.1: misma fórmula que la caja,
  // centralizada en el util `remuneracionVigenteParaMes`). Resuelve por la vigencia del mes pedido —
  // NO la de hoy — y devuelve null si ese mes no está cubierto por ninguna vigencia (no se liquida:
  // espejo del `continue` de la proyección de caja). Reemplaza al viejo getRemuneracionVigente, que
  // resolvía siempre contra hoy y liquidaba cualquier mes con la vigencia actual.
  const remVigenteDe = (empleado, año, mes) =>
    remuneracionVigenteParaMes(
      (remuneraciones || []).filter((r) => r.empleado_id === empleado.id),
      año,
      mes
    );

  const monedaPais = pais === "Chile" ? "CLP" : "PEN";
  const formatMonto = (monto) => formatAmount(monto || 0, monedaPais);

  // Mes/año en curso — misma referencia para las tasas (UF/UTM del último día del mes, la
  // convención de los recibos del contador) y para sumar los pagos/adelantos del período.
  const hoyUF = new Date();
  const mesActual = hoyUF.getMonth() + 1;
  const anoActual = hoyUF.getFullYear();
  // Último día del mes en curso: día 0 del mes SIGUIENTE (getMonth()+1). La UF varía a diario y
  // el recibo usa la del cierre de mes (ej. jun → 30-jun, no 28-jun). getUFValue no se toca.
  const fechaTasasActual = new Date(anoActual, hoyUF.getMonth() + 1, 0).toISOString().split("T")[0];
  const ufActual = ratesListas ? getUFValue(fechaTasasActual) : 0;
  const utmActual = ratesListas ? getUTMValue(fechaTasasActual) : 0;

  // Adelantos ya pagados del empleado en el mes/año en curso (state pagosEmpleados, sin queries).
  const adelantosDelMes = (empleadoId) =>
    pagosEmpleados
      .filter(
        (p) =>
          p.empleado_id === empleadoId &&
          p.tipo === "adelanto" &&
          p.periodo_año === anoActual &&
          p.periodo_mes === mesActual
      )
      .reduce((s, p) => s + Number(p.monto || 0), 0);

  // Haberes imponibles del empleado en un período (state `haberes`, sin queries). Array
  // {monto, imponible} → se pasa como haberesMes a calcularLiquidacionChile.
  const haberesDelMes = (empleadoId, año, mes) =>
    (haberes || []).filter((h) => h.empleado_id === empleadoId && h.periodo_año === año && h.periodo_mes === mes);

  // Comisión materializada (haberes tipo 'comision') de un empleado en un período.
  const comisionDelPeriodo = (empleadoId, año, mes) =>
    (haberes || [])
      .filter(
        (h) => h.empleado_id === empleadoId && h.periodo_año === año && h.periodo_mes === mes && h.tipo === "comision"
      )
      .reduce((s, h) => s + Number(h.monto || 0), 0);

  // Adelantos ya registrados de un empleado en un período (generaliza adelantosDelMes).
  const adelantosDelPeriodo = (empleadoId, año, mes) =>
    pagosEmpleados
      .filter(
        (p) => p.empleado_id === empleadoId && p.tipo === "adelanto" && p.periodo_año === año && p.periodo_mes === mes
      )
      .reduce((s, p) => s + Number(p.monto || 0), 0);

  // ¿El sueldo de un período ya fue pagado? (existe pago tipo 'sueldo' del período).
  const sueldoPagadoEnMes = (empleadoId, año, mes) =>
    pagosEmpleados.some(
      (p) => p.empleado_id === empleadoId && p.tipo === "sueldo" && p.periodo_año === año && p.periodo_mes === mes
    );

  // UF/UTM del último día de un mes (misma convención que las tasas del mes en curso).
  const tasasDelMes = (año, mes) => {
    if (!ratesListas) return { uf: 0, utm: 0 };
    const f = new Date(año, mes, 0).toISOString().split("T")[0]; // día 0 del mes siguiente = último día
    return { uf: getUFValue(f), utm: getUTMValue(f) };
  };

  // Próximo período a liquidar: si el sueldo del mes en curso ya está pagado → mes siguiente; si no,
  // el mes en curso (el sueldo del mes todavía por pagar).
  const proximoPeriodo = (empleadoId) => {
    if (!sueldoPagadoEnMes(empleadoId, anoActual, mesActual)) return { año: anoActual, mes: mesActual };
    return mesActual === 12 ? { año: anoActual + 1, mes: 1 } : { año: anoActual, mes: mesActual + 1 };
  };

  // Override manual de fecha de pago (pago_sueldo_fecha) para un empleado/período, o null.
  const overrideFechaPago = (empleadoId, año, mes) =>
    (fechasPago || []).find((f) => f.empleado_id === empleadoId && f.periodo_año === año && f.periodo_mes === mes)
      ?.fecha_pago || null;

  // Fila de la tabla. Total imponible y Líquido salen de calcularLiquidacionChile —
  // la MISMA función que usa el modal de Liquidación — para que la tabla y la
  // liquidación real del empleado no puedan divergir. La card anterior sumaba solo
  // sueldo base + gratificación y se comía los bonos (Franco: 1.695.044 vs 1.866.086).
  const getFilaEmpleado = (emp) => {
    const rem = remVigenteDe(emp, anoActual, mesActual);
    const sueldoBase = Number(rem?.sueldo_base) || Number(emp.sueldo_base) || 0;
    const bonos = (Number(rem?.bonos_fijo) || 0) + (Number(rem?.bonos_variable) || 0);

    const adelantos = adelantosDelMes(emp.id);
    const comisionMes = comisionDelPeriodo(emp.id, anoActual, mesActual);

    // Bloque PRÓXIMO SUELDO — fecha estimada del próximo período (override si hay). Chile y Perú.
    const { año: pAño, mes: pMes } = proximoPeriodo(emp.id);
    const fechaProximo = overrideFechaPago(emp.id, pAño, pMes) || fechaPagoEstimada(pAño, pMes);

    // Perú no pasa por la liquidación chilena (gratificación y descuentos son otros; sin NETO EST).
    if (emp.pais !== "Chile") {
      return {
        emp,
        sueldoBase,
        gratificacion: null,
        bonos,
        totalImponible: sueldoBase + bonos,
        descuentos: null,
        adelantos,
        neto: null,
        comisionMes,
        fechaProximo,
        netoEstProximo: null,
      };
    }

    const liq = calcularLiquidacionChile(
      rem || { sueldo_base: sueldoBase },
      ufActual,
      0,
      utmActual,
      haberesDelMes(emp.id, anoActual, mesActual)
    );
    const liquidoBruto = rem ? liq.liquido : null;

    // NETO del mes en curso: MISMA fuente que Flujo de Caja (saldoLiquidoPorPeriodo en
    // pagosEmpleados.js, regla 10.1) para que Empleados y Caja no diverjan. Imputación ESTRICTA por
    // período: saldo = devengado − pagado del mes (ancla al recibo + allowlist P3.17 intactas; sin
    // max(0), sin arrastre). Reemplaza el viejo `max(0, motor − sumarPagadoLiquido)` mono-mes, que no
    // anclaba (mostraba el "fantasma" de un mes cerrado sobre-estimado) ni compartía código con Caja.
    let netoMes = null;
    if (liquidoBruto !== null) {
      const { subledger } = saldoLiquidoPorPeriodo({
        devengados: [{ año: anoActual, mes: mesActual, devengadoMotor: liq.liquido }],
        pagos: pagosEmpleados,
        empleadoId: emp.id,
      });
      netoMes = subledger[0].saldo;
    }

    // NETO EST. del próximo período: líquido del motor (incluye la comisión ya materializada de ese
    // período vía haberesDelMes) menos los adelantos ya registrados de ese período. max(0, …).
    const { uf: ufP, utm: utmP } = tasasDelMes(pAño, pMes);
    const liqProximo = calcularLiquidacionChile(
      rem || { sueldo_base: sueldoBase },
      ufP,
      0,
      utmP,
      haberesDelMes(emp.id, pAño, pMes)
    );
    const netoEstProximo = rem ? Math.max(0, liqProximo.liquido - adelantosDelPeriodo(emp.id, pAño, pMes)) : null;

    return {
      emp,
      sueldoBase: liq.sueldoBase,
      gratificacion: liq.gratificacion,
      bonos: liq.bonosFijo + liq.bonosVariable,
      totalImponible: liq.totalImponible,
      // Descuentos legales (AFP + salud + cesantía + impuesto único), tal como los suma
      // calcularLiquidacionChile — el desglose completo vive en el modal Liquidación.
      // Sin remuneración vigente no hay datos previsionales: el modal también se niega a
      // calcular en ese caso. Mostramos "—" en vez de un número inventado.
      descuentos: rem ? liq.totalDescuentosLegales : null,
      adelantos,
      comisionMes,
      fechaProximo,
      netoEstProximo,
      neto: netoMes,
    };
  };

  // Último período liquidado (basado en pagos_empleados, tipo 'sueldo').
  // Formato "MM - YYYY" (ej. "06 - 2026"); el encabezado ya dice "liquidado".
  const getMesLiquidado = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) => {
        if (b.periodo_año !== a.periodo_año) return b.periodo_año - a.periodo_año;
        return b.periodo_mes - a.periodo_mes;
      });
    if (!sueldos[0]) return null;
    return `${String(sueldos[0].periodo_mes).padStart(2, "0")} - ${sueldos[0].periodo_año}`;
  };

  // Último período con pago tipo 'sueldo' como { año, mes } numérico (para el recibo PDF).
  const getUltimoPeriodoLiquidado = (empleadoId) => {
    const sueldos = pagosEmpleados
      .filter((p) => p.empleado_id === empleadoId && p.tipo === "sueldo")
      .sort((a, b) =>
        b.periodo_año !== a.periodo_año ? b.periodo_año - a.periodo_año : b.periodo_mes - a.periodo_mes
      );
    return sueldos[0] ? { año: sueldos[0].periodo_año, mes: sueldos[0].periodo_mes } : null;
  };

  // Descarga el recibo del último período liquidado. Usa EL MISMO motor/inputs que el modal
  // Liquidación (calcularLiquidacionChile con rem vigente + UF/UTM del último día del mes +
  // haberes del período) → coincide al peso. Solo Chile (el motor es chileno).
  const handleDescargarRecibo = async (emp) => {
    const periodo = getUltimoPeriodoLiquidado(emp.id);
    if (!periodo || emp.pais !== "Chile") return;
    const rem = remVigenteDe(emp, periodo.año, periodo.mes);
    if (!rem) {
      alert("Sin remuneración vigente para ese mes: no se puede generar el recibo.");
      return;
    }
    const { uf, utm } = tasasDelMes(periodo.año, periodo.mes);
    const liq = calcularLiquidacionChile(rem, uf, 0, utm, haberesDelMes(emp.id, periodo.año, periodo.mes));
    try {
      await generarReciboPDF(emp, { ...liq, rem, uf, utm }, periodo.año, periodo.mes);
    } catch (e) {
      console.error("Error al generar el recibo PDF:", e);
      alert("Error al generar el recibo PDF");
    }
  };

  // Map RUT normalizado → empleado, para que el parser (10.7) identifique/cruce el PDF subido contra
  // la ficha real — SOLO Chile (10.267), mismo criterio que el script de carga batch (PASO C1).
  const empleadosPorRutChile = () => {
    const m = new Map();
    for (const e of empleados) {
      if (e.pais === "Chile" && e.rut) m.set(e.rut.replace(/\s/g, "").toUpperCase(), e);
    }
    return m;
  };

  // Parsea un PDF de liquidación (extracción de texto en cliente + parser real) — REUSADO por la
  // carga individual (por fila) y la carga masiva (botón general "Cargar liquidaciones", 10.1).
  // Empleado/período SIEMPRE del CONTENIDO del PDF (RUT + "Liquidación de Sueldo de <Mes> de
  // <Año>"), nunca del nombre del archivo (10.7) — ni siquiera se le pasa el nombre al parser.
  const parsearArchivoLiquidacionContadora = async (file) => {
    const texto = await extraerTextoLiquidacionPdf(file);
    return parsearLiquidacionTexto(texto, { empleadosPorRut: empleadosPorRutChile() });
  };

  // Sube el PDF (path versionado, nunca pisa un archivo existente) + RPC atómica que reemplaza la
  // vigente del período (10.202) — MISMO camino para carga individual y masiva (10.1).
  const guardarUnaLiquidacionContadora = async (data, file, version) => {
    const path = await subirLiquidacionPdf(data.empleado_id, data.periodo_año, data.periodo_mes, version, file);
    return guardarLiquidacionContadora(data, path, file.name, usuarioActualId);
  };

  // ─── Modal Liquidaciones de la contadora (PASO C2): abrir + listar ────────────────────────────
  const handleOpenLiquidacionesContadora = async (emp) => {
    // Gate ANTES de cargar datos (10.279) — defensa en profundidad: el kebab ya no ofrece este ítem
    // para una fila ajena si no sos admin, pero igual se corta acá si de algún modo se invocara.
    const esPropia = emp.usuario_id != null && emp.usuario_id === usuarioActualId;
    if (!esAdminLiquidaciones && !esPropia) return;

    setLiquidacionesEmpleado(emp);
    setArchivoLiquidacionSel(null);
    setPreviewLiquidacion(null);
    const hoy = new Date();
    setLiquidacionesAnoSel(hoy.getFullYear());
    setLiquidacionesMesSel(hoy.getMonth() + 1);
    setReciboAbierto(false);
    setAnalizarLiquidaciones(false);
    setComisionAbierta(false);
    setDetalleComisionCache({});
    setLiquidacionesCargandoLista(true);
    try {
      const data = await getLiquidacionesContadoraEmpleado(emp.id);
      setLiquidacionesLista(data || []);
    } catch (e) {
      console.error("Error al cargar liquidaciones de la contadora:", e);
      toast.error("Error al cargar las liquidaciones de la contadora");
      setLiquidacionesLista([]);
    } finally {
      setLiquidacionesCargandoLista(false);
    }
    try {
      await Promise.all([loadExchangeRates(), loadUTM()]);
    } catch (e) {
      console.error("Error cargando tasas:", e);
    }
  };

  // Selecciona un PDF → extrae texto (cliente, pdfjs) → parsea → preview de SOLO LECTURA (nunca
  // guarda sin confirmación explícita). Si el parser rechaza, muestra el motivo — nunca se ignora
  // (10.177). Si el RUT del PDF matchea a OTRO empleado (no al de este modal), también se rechaza:
  // aunque el PDF sea válido, cargarlo acá sería vincularlo a la ficha equivocada (10.7).
  const handleSeleccionArchivoLiquidacion = async (file) => {
    setArchivoLiquidacionSel(file || null);
    setPreviewLiquidacion(null);
    if (!file) return;
    setProcesandoArchivoLiquidacion(true);
    try {
      const resultado = await parsearArchivoLiquidacionContadora(file);
      if (resultado.ok && resultado.empleado.id !== liquidacionesEmpleado.id) {
        setPreviewLiquidacion({
          ok: false,
          motivo: `Este PDF corresponde a ${resultado.empleado.nombre} ${resultado.empleado.apellido} (RUT del PDF), no a ${liquidacionesEmpleado.nombre} ${liquidacionesEmpleado.apellido} — no se carga acá.`,
        });
      } else {
        setPreviewLiquidacion(resultado);
      }
    } catch (e) {
      console.error("Error al parsear el PDF de liquidación:", e);
      setPreviewLiquidacion({ ok: false, motivo: `No se pudo leer el PDF: ${e.message}` });
    } finally {
      setProcesandoArchivoLiquidacion(false);
    }
  };

  // Confirma: sube el PDF (path versionado, nunca pisa uno existente) + RPC atómica que apaga la
  // vigente anterior de ese período e inserta la nueva (10.202) — MISMO camino que el script de
  // carga batch (10.1). cargadoPor = usuarioActualId REAL de la sesión (10.104).
  const handleConfirmarCargaLiquidacion = async () => {
    if (!previewLiquidacion?.ok || !liquidacionesEmpleado || !archivoLiquidacionSel) return;
    setGuardandoLiquidacionContadora(true);
    try {
      const { data } = previewLiquidacion;
      const versionesExistentes = liquidacionesLista.filter(
        (l) => l.periodo_año === data.periodo_año && l.periodo_mes === data.periodo_mes
      ).length;
      await guardarUnaLiquidacionContadora(data, archivoLiquidacionSel, versionesExistentes + 1);
      toast.success("Liquidación cargada correctamente");
      setArchivoLiquidacionSel(null);
      setPreviewLiquidacion(null);
      const refreshed = await getLiquidacionesContadoraEmpleado(liquidacionesEmpleado.id);
      setLiquidacionesLista(refreshed || []);
    } catch (e) {
      console.error("Error al guardar la liquidación de la contadora:", e);
      toast.error("Error al guardar la liquidación");
    } finally {
      setGuardandoLiquidacionContadora(false);
    }
  };

  const handleDescargarLiquidacionContadora = async (fila) => {
    try {
      const url = await getUrlLiquidacionPdf(fila.archivo_path);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      console.error("Error al generar el link de descarga:", e);
      toast.error("No se pudo generar el link de descarga");
    }
  };

  // Arma la lista de "Haberes imponibles" ESTIMADA (cuando no hay liquidación de la contadora
  // cargada para el mes) con el MISMO desglose que ya usa el modal "Ver liquidación" (10.1): sueldo
  // base, bonos de la remuneración vigente, gratificación, una línea "Comisión" (suma de los haberes
  // tipo='comision' del mes — la contadora los legalmente etiqueta "Bono <mes>", ver más abajo el
  // detalle de comisión) y cada otro haber suelto con su propio concepto. NUNCA se relabela un haber
  // — "si un haber es manual sin referencia, mostrar su concepto tal cual" (pedido explícito).
  const construirHaberesEstimado = (liq, haberesMes) => {
    const lineas = [{ etiqueta: "Sueldo base", monto: liq.sueldoBase }];
    if (liq.bonosFijo > 0) lineas.push({ etiqueta: "Bono fijo (contrato)", monto: liq.bonosFijo });
    if (liq.bonosVariable > 0) lineas.push({ etiqueta: "Bono variable (contrato)", monto: liq.bonosVariable });
    lineas.push({ etiqueta: "Gratificación", monto: liq.gratificacion });
    const totalComision = (haberesMes || [])
      .filter((h) => h.tipo === "comision")
      .reduce((s, h) => s + (Number(h.monto) || 0), 0);
    if (totalComision > 0) lineas.push({ etiqueta: "Comisión", monto: totalComision });
    for (const h of haberesMes || []) {
      if (h.tipo === "comision") continue; // ya sumada en la línea "Comisión" de arriba
      lineas.push({ etiqueta: h.concepto || (h.tipo === "bono" ? "Bono" : "Otro haber"), monto: Number(h.monto) || 0 });
    }
    return lineas;
  };

  // Arma TODO el dato de un mes puntual del recibo: CONTADORA (liquidaciones_contadora vigente, si
  // existe) o, si no, PLATAFORMA estimada (calcularLiquidacionChile, MISMOS inputs que "Ver
  // liquidación" — 10.1) etiquetada "(estimado)". El líquido/desglose que se MUESTRA siempre sale de
  // UNA sola fuente por mes (nunca se mezclan campos de las dos) — la fuente completa queda en
  // `plataforma` aparte, para la comparación de "Analizar" (admin).
  //
  // PAGADO/SALDO: `sumarPagadoRecibo`/`pagosDelMesTransferido` (pagosEmpleados.js) — pagos REALES
  // (con movimiento_bancario_id) agrupados por el MES DE LA TRANSFERENCIA, no por el período que
  // cada fila declara (una comisión materializada por mes de cobro puede transferirse junto con el
  // sueldo de un mes posterior). Verificado con la DB real: Nicolás Soto jun-2026 $869.184 vs
  // $869.184, ago-2026 $1.087.295 vs $1.087.295 (0 diferencia). Los pagos SIN movimiento bancario
  // (ej. backfill de comisión sin transferencia propia) se listan aparte, nunca suman.
  const datosDelMes = (emp, año, mes) => {
    const contadora = liquidacionesLista.find((l) => l.periodo_año === año && l.periodo_mes === mes && l.vigente);
    const rem = remVigenteDe(emp, año, mes);
    const { uf, utm } = tasasDelMes(año, mes);
    const haberesMes = haberesDelMes(emp.id, año, mes);
    const plataforma = rem ? calcularLiquidacionChile(rem, uf, 0, utm, haberesMes) : null;
    const esEstimado = !contadora;

    const haberesLineas = contadora
      ? contadora.haberes || []
      : plataforma
        ? construirHaberesEstimado(plataforma, haberesMes)
        : [];
    const totalBruto = contadora ? contadora.total_haberes : (plataforma?.totalImponible ?? null);
    const descLegales = contadora
      ? {
          afp: contadora.afp_monto,
          salud: contadora.salud_monto,
          cesantia: contadora.cesantia_monto,
          impuesto: contadora.impuesto_unico_monto,
          total: contadora.total_descuentos_legales,
        }
      : plataforma
        ? {
            afp: plataforma.totalAfp,
            salud: plataforma.totalSalud,
            cesantia: plataforma.segCesantia,
            impuesto: plataforma.impuestoUnico,
            total: plataforma.totalDescuentosLegales,
          }
        : null;
    const otrosDescLineas = contadora ? contadora.descuentos_varios || [] : [];
    const totalOtrosDesc = contadora ? contadora.total_descuentos_varios : 0;
    const liquido = contadora ? contadora.liquido : (plataforma?.liquido ?? null);

    const pagosReales = pagosDelMesTransferido(pagosEmpleados, emp.id, año, mes);
    const pagadoTotal = sumarPagadoRecibo(pagosEmpleados, emp.id, año, mes);
    const pagosSinTransferencia = pagosSinMovimientoBancario(pagosEmpleados, emp.id, año, mes);
    const saldo = liquido != null ? liquido - pagadoTotal : null;
    const comisionesDelMes = haberesMes.filter((h) => h.tipo === "comision");

    return {
      contadora,
      plataforma,
      esEstimado,
      uf,
      utm,
      haberesLineas,
      totalBruto,
      descLegales,
      otrosDescLineas,
      totalOtrosDesc,
      liquido,
      pagosReales,
      pagadoTotal,
      pagosSinTransferencia,
      saldo,
      comisionesDelMes,
    };
  };

  // Click en la fila de un mes → abre/cierra su recibo. Cambiar de mes colapsa cualquier detalle de
  // comisión que hubiera quedado abierto del mes anterior (evita mostrar un desglose de OTRO mes).
  const toggleMesRecibo = (mes) => {
    if (liquidacionesMesSel === mes && reciboAbierto) {
      setReciboAbierto(false);
      return;
    }
    setLiquidacionesMesSel(mes);
    setReciboAbierto(true);
    setComisionAbierta(false);
  };

  // Despliega "de qué ventas sale" la comisión del mes (a partir de haberes_empleado, 10.1 — nunca
  // reimplementa el motor de comisión en vivo). Cachea por año-mes: resolverVentasDeComision hace
  // queries reales a sales/sales_contratos/clientes, no se repiten si ya se resolvió ese mes.
  const toggleComisionRecibo = async (año, mes, comisionesDelMes) => {
    if (comisionAbierta) {
      setComisionAbierta(false);
      return;
    }
    setComisionAbierta(true);
    const key = `${año}-${mes}`;
    if (detalleComisionCache[key]) return;
    setCargandoComision(true);
    try {
      const resuelto = await resolverVentasDeComision(comisionesDelMes);
      setDetalleComisionCache((prev) => ({ ...prev, [key]: resuelto }));
    } catch (e) {
      console.error("Error al resolver el detalle de comisión:", e);
      toast.error("No se pudo resolver el detalle de la comisión");
    } finally {
      setCargandoComision(false);
    }
  };

  // ─── Carga masiva de liquidaciones (botón general "Cargar liquidaciones", solo admin) ─────────
  const handleAbrirCargaMasiva = () => {
    if (!esAdminLiquidaciones) return;
    setFilasMasivas([]);
    setResultadoMasivo(null);
    setShowCargaMasiva(true);
  };

  // Selecciona VARIOS PDF a la vez → parsea CADA UNO con el mismo util validado (empleado/período
  // siempre del contenido, 10.7) → arma la tabla de revisión. Detecta, además del rechazo del
  // parser, 2 casos que el parser no ve por sí solo: (a) "duplicado en este lote" — 2+ archivos
  // resuelven al MISMO empleado+período (rechaza el segundo en adelante, 10.177 — nunca elige uno
  // a ciegas), y (b) "ya existe esa liquidación" — ya hay una vigente en la base para ese
  // empleado+período (informativo, NO bloquea: la RPC igual la reemplaza por versión nueva sin
  // perder la anterior, 10.202). Una sola query batcheada para (b), nunca N+1 por archivo.
  const handleSeleccionArchivosMasivos = async (fileList) => {
    const files = fileList ? [...fileList] : [];
    setResultadoMasivo(null);
    if (files.length === 0) {
      setFilasMasivas([]);
      return;
    }
    setProcesandoMasiva(true);
    try {
      const parseados = [];
      for (const file of files) {
        let resultado;
        try {
          // eslint-disable-next-line no-await-in-loop
          resultado = await parsearArchivoLiquidacionContadora(file);
        } catch (e) {
          resultado = { ok: false, motivo: `No se pudo leer el PDF: ${e.message}` };
        }
        parseados.push({ archivo: file.name, file, resultado });
      }

      // Duplicados DENTRO de este lote: mismo empleado+período en 2+ archivos que parsearon OK.
      const vistos = new Map(); // "empleadoId-año-mes" → nombre del primer archivo
      for (const fila of parseados) {
        if (!fila.resultado.ok) continue;
        const clave = `${fila.resultado.empleado.id}-${fila.resultado.data.periodo_año}-${fila.resultado.data.periodo_mes}`;
        if (vistos.has(clave)) fila.duplicadoDe = vistos.get(clave);
        else vistos.set(clave, fila.archivo);
      }

      // "Ya existe" — 1 query batcheada contra los empleados que aparecen en archivos OK.
      const empleadoIds = [...new Set(parseados.filter((f) => f.resultado.ok).map((f) => f.resultado.empleado.id))];
      const existentes = empleadoIds.length > 0 ? await getLiquidacionesContadoraPorEmpleados(empleadoIds) : [];

      const filas = parseados.map((fila) => {
        if (!fila.resultado.ok) {
          return { ...fila, estado: "rechazado", motivo: fila.resultado.motivo };
        }
        if (fila.duplicadoDe) {
          return {
            ...fila,
            estado: "rechazado",
            motivo: `Mismo empleado y período que "${fila.duplicadoDe}" — PDF duplicado en este lote.`,
          };
        }
        const { empleado, data } = fila.resultado;
        const previas = existentes.filter(
          (l) =>
            l.empleado_id === empleado.id && l.periodo_año === data.periodo_año && l.periodo_mes === data.periodo_mes
        );
        const yaVigente = previas.some((l) => l.vigente);
        return {
          ...fila,
          estado: yaVigente ? "ya_existe" : "ok",
          motivo: yaVigente
            ? "Ya hay una liquidación vigente para este empleado y período — esto va a cargar una versión nueva."
            : null,
          version: previas.length + 1,
        };
      });
      setFilasMasivas(filas);
    } finally {
      setProcesandoMasiva(false);
    }
  };

  // Confirma el lote: guarda cada fila "ok"/"ya_existe" (NUNCA las "rechazado", el botón ya está
  // deshabilitado si hay alguna) con el MISMO camino de escritura que la carga individual
  // (guardarUnaLiquidacionContadora — 10.1: sube el PDF + RPC atómica). Secuencial, no en paralelo
  // (evita 27 uploads simultáneos y deja el progreso legible). Si UNA falla a mitad, se FRENA ahí
  // mismo y se informa explícito qué archivo y cuántas quedaron guardadas ANTES de esa (10.177) —
  // las ya guardadas son filas completas (PDF + fila en la tabla, nunca una sin la otra) y se
  // dejan tal cual, nunca se revierten: son trabajo válido y completo, no hay razón para deshacerlo.
  const handleConfirmarCargaMasiva = async () => {
    const pendientes = filasMasivas.filter((f) => f.estado === "ok" || f.estado === "ya_existe");
    if (pendientes.length === 0) return;
    setGuardandoMasiva(true);
    setResultadoMasivo(null);
    let guardados = 0;
    try {
      for (const fila of pendientes) {
        setProgresoMasivo({ hechos: guardados, total: pendientes.length, archivo: fila.archivo });
        try {
          // eslint-disable-next-line no-await-in-loop
          await guardarUnaLiquidacionContadora(fila.resultado.data, fila.file, fila.version);
          guardados++;
        } catch (e) {
          console.error("Error al guardar liquidación (carga masiva):", e);
          setResultadoMasivo({
            guardados,
            total: pendientes.length,
            error: `Se frenó en "${fila.archivo}" (van ${guardados}/${pendientes.length} guardadas): ${e.message}`,
          });
          return;
        }
      }
      setResultadoMasivo({ guardados, total: pendientes.length, error: null });
      setFilasMasivas([]);
      // Remonta el <input type="file"> (key nueva) para limpiar la selección nativa del picker —
      // setFilasMasivas([]) ya limpiaba la tabla de revisión, pero el input no controlado seguía
      // mostrando los nombres de los archivos recién cargados.
      setCargaMasivaInputKey((k) => k + 1);
      toast.success(`${guardados} liquidación(es) cargadas correctamente`);
      // Si el modal individual está abierto para alguno de los empleados recién cargados, refresca
      // su lista para que se vea sin tener que cerrar y reabrir.
      if (liquidacionesEmpleado) {
        try {
          const refreshed = await getLiquidacionesContadoraEmpleado(liquidacionesEmpleado.id);
          setLiquidacionesLista(refreshed || []);
        } catch (e) {
          console.error("Error al refrescar liquidaciones tras la carga masiva:", e);
        }
      }
    } finally {
      setGuardandoMasiva(false);
      setProgresoMasivo(null);
    }
  };

  const handleOpenForm = (empleado = null) => {
    if (empleado) {
      setFormData({
        ...empleado,
        fecha_nacimiento: empleado.fecha_nacimiento || "",
        fin_contrato: empleado.fin_contrato || "",
        rut: empleado.rut || "",
        cargo: empleado.cargo || "",
        telefono: empleado.telefono || "",
        email: empleado.email || "",
        devenga_vacaciones: empleado.devenga_vacaciones ?? true,
      });
      setEmpleadoSeleccionado(empleado);
    } else {
      setFormData({
        nombre: "",
        apellido: "",
        fecha_nacimiento: "",
        inicio_contrato: "",
        fin_contrato: "",
        rut: "",
        cargo: "",
        telefono: "",
        email: "",
        devenga_vacaciones: true,
        pais,
      });
      setEmpleadoSeleccionado(null);
    }
  };

  const handleSaveEmpleado = async () => {
    try {
      // sueldo_base/comision_porcentaje ya NO se escriben (huérfanos tras 4.1a; el sueldo vive en
      // remuneraciones_empleado). La columna se conserva en DB pero la ficha no la toca.
      const cleanedData = {
        nombre: formData.nombre.trim(),
        apellido: formData.apellido.trim(),
        fecha_nacimiento: formData.fecha_nacimiento || null,
        inicio_contrato: formData.inicio_contrato,
        fin_contrato: formData.fin_contrato || null,
        rut: formData.rut?.trim() || null,
        cargo: formData.cargo?.trim() || null,
        telefono: formData.telefono?.trim() || null,
        email: formData.email?.trim() || null,
        devenga_vacaciones: formData.devenga_vacaciones ?? true,
        pais,
      };

      const resultado = empleadoSeleccionado
        ? await actualizarEmpleado(empleadoSeleccionado.id, cleanedData)
        : await crearEmpleado(cleanedData);
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
      // Recién creado (sin empleadoSeleccionado previo): seleccionarlo en el acto para que
      // Haberes/Vacaciones/Liquidaciones/el resto de la Ficha queden operativos sin un paso extra.
      if (!empleadoSeleccionado && resultado?.[0]) {
        handleSeleccionarEmpleado(resultado[0]);
      }
      toast.success("Empleado guardado correctamente");
    } catch (error) {
      console.error("Error al guardar empleado:", error);
      alert("Error al guardar empleado");
    }
  };

  const handleOpenVacaciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    try {
      const data = await getVacacionesEmpleado(empleado.id);
      setVacaciones(data || []);
    } catch {
      setVacaciones([]);
    }
  };

  const handleAddVacacion = async () => {
    try {
      await agregarVacaciones({
        empleado_id: empleadoSeleccionado.id,
        ...vacacionForm,
      });
      setVacacionForm({ fecha_inicio: "", fecha_fin: "", notas: "" });
      const data = await getVacacionesEmpleado(empleadoSeleccionado.id);
      setVacaciones(data || []);
    } catch {
      alert("Error al agregar vacaciones");
    }
  };

  // Re-carga los haberes del país al state `haberes`. Se llama tras alta/baja para que el líquido
  // de la tabla (getFilaEmpleado) y del modal Liquidación se recalculen en el acto.
  const refreshHaberes = async () => {
    try {
      const data = await getHaberesPais(pais);
      setHaberes(data || []);
    } catch (e) {
      console.error("Error al refrescar haberes:", e);
    }
  };

  const handleOpenHaberes = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setHaberMontoFocus(false);
    setHaberPeriodo({ mes: new Date().getMonth() + 1, ano: new Date().getFullYear() });
    setHaberForm({ tipo: "bono", monto: "", concepto: "", imponible: true });
  };

  const handleAddHaber = async () => {
    const monto = parseFloat(haberForm.monto) || 0;
    if (!empleadoSeleccionado || monto <= 0) {
      alert("Ingresá un monto mayor a 0.");
      return;
    }
    try {
      await crearHaberEmpleado({
        empleado_id: empleadoSeleccionado.id,
        periodo_año: Number(haberPeriodo.ano),
        periodo_mes: Number(haberPeriodo.mes),
        tipo: haberForm.tipo,
        monto,
        imponible: haberForm.imponible,
        concepto: haberForm.concepto.trim() || null,
        origen: "manual",
      });
      setHaberForm({ tipo: "bono", monto: "", concepto: "", imponible: true });
      await refreshHaberes();
    } catch {
      alert("Error al agregar el haber");
    }
  };

  const handleDeleteHaber = async (id) => {
    if (!window.confirm("¿Eliminar este haber?")) return;
    try {
      await eliminarHaberEmpleado(id);
      await refreshHaberes();
    } catch {
      alert("Error al eliminar el haber");
    }
  };

  // Monto numérico (posible "958925.00") → string de dígitos enteros ("958925") para el form.
  const montoADigitos = (v) => (v === "" || v == null ? "" : String(Math.round(Number(v) || 0)));

  // Arma el estado del form a partir de una fila de remuneración. Las fechas se pasan aparte:
  // al PRECARGAR (Agregar) van en blanco (el usuario define el nuevo inicio); al EDITAR van
  // las fechas reales de la fila.
  const remFormDesde = (rem, { inicio_vigencia = "", fin_vigencia = "" } = {}) => ({
    inicio_vigencia,
    fin_vigencia,
    sueldo_base: montoADigitos(rem.sueldo_base),
    comision_porcentaje: rem.comision_porcentaje ?? "",
    bonos_fijo: montoADigitos(rem.bonos_fijo),
    bonos_variable: montoADigitos(rem.bonos_variable),
    afp_nombre: rem.afp_nombre || "",
    afp_cap_individual_pct: rem.afp_cap_individual_pct ?? "",
    afp_comision_pct: rem.afp_comision_pct ?? "",
    salud_tipo: rem.salud_tipo || "",
    salud_isapre_uf: rem.salud_isapre_uf ?? "",
  });

  const handleOpenRemuneraciones = async (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setRemuneracionEditando(null);
    setMontoFocus(null);
    let data = [];
    try {
      data = (await getRemuneracionesEmpleado(empleado.id)) || [];
      setRemuneraciones(data);
    } catch {
      setRemuneraciones([]);
    }
    // Precarga con la vigencia vigente (fin_vigencia null) como plantilla del alta nueva;
    // inicio_vigencia queda en blanco para que el usuario fije el nuevo inicio.
    const vigente = data.find((r) => !r.fin_vigencia);
    setRemuneracionForm(vigente ? remFormDesde(vigente) : REM_FORM_VACIO);
  };

  // ─── Modal liquidación: handlers ──────────────────────────
  // Vista de SOLO LECTURA: calcula y muestra el desglose del líquido del mes.
  // NO registra pagos ni vincula movimientos bancarios — el hecho de pago del sueldo
  // se registra únicamente en la caja (VincularMovimientoModal).
  // RETIRADA del flujo (Fase 1 de pestañas, 2026-10-01): sin punto de acceso en la UI —
  // la pestaña Liquidaciones (contadora) la reemplaza. Código conservado a propósito
  // (ver docs/modulos/empleados.md), no se borra sin confirmación.
  // eslint-disable-next-line no-unused-vars
  const handleOpenLiquidacion = async (emp) => {
    setLiquidacionEmpleado(emp);
    const hoy = new Date();
    setLiquidacionMes(hoy.getMonth() + 1);
    setLiquidacionAno(hoy.getFullYear());
    setShowLiquidacion(true);
    try {
      await Promise.all([loadExchangeRates(), loadUTM()]);
    } catch (e) {
      console.error("Error cargando tasas:", e);
    }
  };

  const handleLiquidacionMesChange = (mes, ano) => {
    setLiquidacionMes(mes);
    setLiquidacionAno(ano);
  };

  const handleOpenEgreso = (empleado) => {
    setEmpleadoSeleccionado(empleado);
    setEgresoForm({
      egreso_fecha: empleado.egreso_fecha || "",
      egreso_monto: empleado.egreso_monto || "",
    });
  };

  const handleSaveEgreso = async () => {
    try {
      await actualizarEmpleado(empleadoSeleccionado.id, {
        egreso_fecha: egresoForm.egreso_fecha || null,
        egreso_monto: parseFloat(egresoForm.egreso_monto) || null,
      });
      const data = await getEmpleados(pais);
      setEmpleados(data || []);
      toast.success("Egreso guardado correctamente");
    } catch (error) {
      console.error("Error al guardar egreso:", error);
      toast.error("Error al guardar egreso");
    }
  };

  const handleEditRemuneracion = (rem) => {
    setMontoFocus(null);
    setRemuneracionEditando(rem);
    setRemuneracionForm(
      remFormDesde(rem, { inicio_vigencia: rem.inicio_vigencia, fin_vigencia: rem.fin_vigencia || "" })
    );
  };

  // Día previo a una fecha ISO ("YYYY-MM-DD"). Mediodía para no cruzar de día por TZ.
  const diaPrevio = (isoDate) => {
    const d = new Date(isoDate + "T12:00:00");
    d.setDate(d.getDate() - 1);
    return d.toISOString().split("T")[0];
  };

  const handleSaveRemuneracion = async () => {
    if (!remuneracionForm.inicio_vigencia) {
      alert("Inicio de vigencia es obligatorio");
      return;
    }
    try {
      const numOrNull = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
      const cleanedData = {
        inicio_vigencia: remuneracionForm.inicio_vigencia,
        fin_vigencia: remuneracionForm.fin_vigencia || null,
        sueldo_base: parseFloat(remuneracionForm.sueldo_base) || 0,
        comision_porcentaje: parseFloat(remuneracionForm.comision_porcentaje) || 0,
        bonos_fijo: parseFloat(remuneracionForm.bonos_fijo) || 0,
        bonos_variable: parseFloat(remuneracionForm.bonos_variable) || 0,
        afp_nombre: remuneracionForm.afp_nombre.trim() || null,
        afp_cap_individual_pct: numOrNull(remuneracionForm.afp_cap_individual_pct),
        afp_comision_pct: numOrNull(remuneracionForm.afp_comision_pct),
        salud_tipo: remuneracionForm.salud_tipo || null,
        salud_isapre_uf: remuneracionForm.salud_tipo === "isapre" ? numOrNull(remuneracionForm.salud_isapre_uf) : null,
      };

      let newRemuneraciones;
      if (remuneracionEditando) {
        const result = await actualizarRemuneracion(remuneracionEditando.id, cleanedData);
        newRemuneraciones = remuneraciones.map((r) => (r.id === remuneracionEditando.id ? result[0] : r));
      } else {
        // Auto-cierre: la vigencia abierta anterior (fin_vigencia null e inicio previo al
        // nuevo) se cierra el día antes del nuevo inicio, para no solaparlas a mano.
        const anterior = (remuneraciones || [])
          .filter(
            (r) =>
              r.empleado_id === empleadoSeleccionado.id &&
              !r.fin_vigencia &&
              r.inicio_vigencia < remuneracionForm.inicio_vigencia
          )
          .sort((a, b) => new Date(b.inicio_vigencia) - new Date(a.inicio_vigencia))[0];
        if (anterior) {
          await actualizarRemuneracion(anterior.id, { fin_vigencia: diaPrevio(remuneracionForm.inicio_vigencia) });
        }
        const result = await crearRemuneracion({
          empleado_id: empleadoSeleccionado.id,
          ...cleanedData,
        });
        newRemuneraciones = [...remuneraciones, result[0]];
      }

      // Actualiza el state local del modal
      setRemuneraciones(newRemuneraciones);
      setRemuneracionEditando(null);
      setRemuneracionForm(REM_FORM_VACIO);

      // Recarga empleados para que la tabla principal recalcule subrows
      const data = await getEmpleados(pais);
      setEmpleados(data || []);

      // Recarga remuneraciones del empleado actual para mantener sincronizado el modal
      const updatedRem = await getRemuneracionesEmpleado(empleadoSeleccionado.id);
      setRemuneraciones(updatedRem || []);
    } catch (error) {
      console.error("Error al guardar remuneración:", error);
      alert("Error al guardar remuneración");
    }
  };

  // Input de monto con separador de miles: enfocado muestra dígitos crudos (edición fluida),
  // sin foco muestra el formato de moneda (formatMonto). El state guarda solo dígitos enteros.
  const renderMontoInput = (campo) => {
    const raw = remuneracionForm[campo];
    const display = montoFocus === campo ? raw : raw === "" ? "" : formatMonto(Number(raw));
    return (
      <Form.Control
        type="text"
        inputMode="numeric"
        value={display}
        onFocus={() => setMontoFocus(campo)}
        onBlur={() => setMontoFocus(null)}
        onChange={(e) => setRemuneracionForm((f) => ({ ...f, [campo]: e.target.value.replace(/\D/g, "") }))}
      />
    );
  };

  // Selector de empleado del header (Fase 1): un solo punto de entrada que prepara TODAS las
  // pestañas (Ficha/Haberes/Vacaciones/Liquidaciones) para el empleado elegido, reusando los
  // mismos handlers que antes abrían cada modal por separado (10.1) — cada uno ya sabe cargar
  // su propio dato (vacaciones, remuneraciones, haberes del período, liquidaciones de la
  // contadora); acá solo se los dispara juntos, sin el `setShowX(true)` que ya no existe.
  const handleSeleccionarEmpleado = (empleado) => {
    if (!empleado) return;
    handleOpenForm(empleado);
    handleOpenVacaciones(empleado);
    handleOpenHaberes(empleado);
    handleOpenRemuneraciones(empleado);
    handleOpenEgreso(empleado);
    handleOpenLiquidacionesContadora(empleado);
  };

  // Lista del selector de empleado del header: todos (activos + egresados), orden alfabético.
  const empleadosOrdenados = [...empleados].sort((a, b) =>
    `${a.apellido} ${a.nombre}`.localeCompare(`${b.apellido} ${b.nombre}`)
  );

  if (loading) return <Loader />;

  return (
    <div className={styles.container}>
      <Header
        leftContent={
          // Barra de pestañas — MISMO estilo inline que las sub-tabs de EstadoCuenta y el grid del
          // hub de Ventas (border 1px #dee2e6, pill 32px, activo fondo #2c3e50/texto blanco): se
          // reusa el patrón, nunca un componente nuevo (pedido explícito de Ignacio).
          <div style={{ display: "flex", alignItems: "center", marginLeft: 24 }}>
            <div
              style={{
                display: "flex",
                gap: 0,
                borderRadius: 6,
                overflow: "hidden",
                border: "1px solid #dee2e6",
              }}
            >
              {TABS_EMPLEADOS.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveTab(t.key)}
                  style={{
                    height: 32,
                    padding: "0 18px",
                    fontSize: "0.82rem",
                    fontWeight: 400,
                    border: "none",
                    cursor: "pointer",
                    background: activeTab === t.key ? "#2c3e50" : "transparent",
                    color: activeTab === t.key ? "#fff" : "#666",
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        }
      >
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          {activeTab === "equipo" ? (
            <Form.Check
              type="checkbox"
              id="ver-egresados"
              label="Ver egresados"
              checked={showEgresados}
              onChange={(e) => setShowEgresados(e.target.checked)}
            />
          ) : (
            <>
              <Form.Select
                size="sm"
                style={{ width: 230 }}
                value={empleadoSeleccionado?.id ?? ""}
                onChange={(e) =>
                  handleSeleccionarEmpleado(empleadosOrdenados.find((emp) => String(emp.id) === e.target.value))
                }
              >
                <option value="">Elegí un empleado…</option>
                {empleadosOrdenados.map((emp) => {
                  const egresado = emp.fin_contrato && new Date(emp.fin_contrato) <= new Date();
                  return (
                    <option key={emp.id} value={emp.id}>
                      {emp.apellido} {emp.nombre}
                      {egresado ? " (egresado)" : ""}
                    </option>
                  );
                })}
              </Form.Select>
              <Form.Select
                size="sm"
                style={{ width: 100 }}
                value={liquidacionesAnoSel}
                onChange={(e) => {
                  setLiquidacionesAnoSel(Number(e.target.value));
                  setReciboAbierto(false);
                  setComisionAbierta(false);
                }}
              >
                {[2024, 2025, 2026, 2027].map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </Form.Select>
            </>
          )}
          {esAdminLiquidaciones && (
            <Button
              variant="primary"
              onClick={() => {
                handleOpenForm();
                setActiveTab("ficha");
              }}
              className={styles.nuevoBtn}
            >
              Nuevo Empleado
            </Button>
          )}
          {esAdminLiquidaciones && (
            <Button variant="outline-primary" onClick={handleAbrirCargaMasiva} className={styles.nuevoBtn}>
              Cargar liquidaciones
            </Button>
          )}
        </div>
      </Header>

      {activeTab === "equipo" && (
        <div className={styles.tablaWrapper}>
          {empleados.length === 0 ? (
            <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
              No hay empleados registrados
            </div>
          ) : (
            (() => {
              // Orden pedido: el que más cuesta arriba → Total imponible descendente.
              const sortFn = (a, b) => b.totalImponible - a.totalImponible;
              const ahora = new Date();
              const activos = [...empleados]
                .filter((e) => !e.fin_contrato || new Date(e.fin_contrato) > ahora)
                .map(getFilaEmpleado)
                .sort(sortFn);
              const egresados = [...empleados]
                .filter((e) => e.fin_contrato && new Date(e.fin_contrato) <= ahora)
                .map(getFilaEmpleado)
                .sort(sortFn);

              const renderFila = (fila, esEgresada) => {
                const emp = fila.emp;
                const badgeLiquidado = getMesLiquidado(emp.id);

                return (
                  <tr key={emp.id} className={esEgresada ? styles.filaEgresada : undefined}>
                    <td className="text-start align-middle">
                      {emp.nombre} {emp.apellido}
                    </td>
                    <td className="text-center align-middle">{formatFecha(emp.inicio_contrato)}</td>
                    <td className="text-center align-middle">
                      {badgeLiquidado || "-"}
                      {badgeLiquidado && emp.pais === "Chile" && (
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          className="ms-2 py-0 px-1"
                          title="Descargar recibo de sueldo (PDF)"
                          onClick={() => handleDescargarRecibo(emp)}
                        >
                          ⬇ recibo
                        </Button>
                      )}
                    </td>
                    <td className="text-end align-middle">{formatMonto(fila.totalImponible)}</td>
                    <td className="text-end align-middle">
                      {fila.comisionMes > 0 ? formatMonto(fila.comisionMes) : "—"}
                    </td>
                    <td className="text-end align-middle">
                      {fila.descuentos === null ? "-" : formatMonto(fila.descuentos)}
                    </td>
                    <td className="text-end align-middle">{fila.adelantos > 0 ? formatMonto(fila.adelantos) : "-"}</td>
                    <td className="text-end align-middle">{fila.neto === null ? "-" : formatMonto(fila.neto)}</td>
                    {/* PRÓXIMO SUELDO */}
                    <td className="text-center align-middle">{formatFecha(fila.fechaProximo)}</td>
                    <td className="text-end align-middle">
                      {fila.netoEstProximo === null ? "-" : formatMonto(fila.netoEstProximo)}
                    </td>
                  </tr>
                );
              };

              return (
                <table className="table table-bordered table-hover align-middle">
                  <thead className="table-dark text-center align-middle">
                    <tr>
                      <th rowSpan={2} style={{ width: "20%" }}>
                        EMPLEADO
                      </th>
                      <th rowSpan={2} style={{ width: "9%" }}>
                        INICIO DE ACTIVIDAD
                      </th>
                      <th rowSpan={2} style={{ width: "11%" }}>
                        ÚLTIMO SUELDO LIQUIDADO
                      </th>
                      <th colSpan={5}>SUELDO DEL MES</th>
                      <th colSpan={2}>PRÓXIMO SUELDO</th>
                    </tr>
                    <tr>
                      <th>BRUTO</th>
                      <th>COMISIÓN</th>
                      <th>DESCUENTOS</th>
                      <th>ADELANTOS</th>
                      <th>NETO</th>
                      <th>FECHA</th>
                      <th>NETO EST.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activos.map((fila) => renderFila(fila, false))}
                    {showEgresados && egresados.map((fila) => renderFila(fila, true))}
                  </tbody>
                </table>
              );
            })()
          )}
        </div>
      )}

      {/* ───────────────────────────────────────────────────────────────────────────────────
          Pestaña Ficha (Datos + Remuneración + Egreso) — Fase 1: contenido de los 3 modales
          anteriores, EXACTO (mismos campos/handlers), solo sin el envoltorio <Modal>.
          ─────────────────────────────────────────────────────────────────────────────────── */}
      {activeTab === "ficha" && (
        <div className={styles.tabPage}>
          {!empleadoSeleccionado && !formData.nombre && (
            <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
              Elegí un empleado en el selector de arriba, o "Nuevo Empleado".
            </div>
          )}
          <div className={styles.tabPageCard}>
            <h5 className="mb-3">Datos</h5>
            <Form>
              <div className="row">
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Nombre</Form.Label>
                  <Form.Control
                    value={formData.nombre}
                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Apellido</Form.Label>
                  <Form.Control
                    value={formData.apellido}
                    onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Fecha Nacimiento</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.fecha_nacimiento}
                    onChange={(e) => setFormData({ ...formData, fecha_nacimiento: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Inicio Contrato</Form.Label>
                  <Form.Control
                    type="date"
                    required
                    value={formData.inicio_contrato}
                    onChange={(e) => setFormData({ ...formData, inicio_contrato: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Fin Contrato (opcional)</Form.Label>
                  <Form.Control
                    type="date"
                    value={formData.fin_contrato}
                    onChange={(e) => setFormData({ ...formData, fin_contrato: e.target.value || null })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>RUT</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="12.345.678-9"
                    value={formData.rut}
                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Cargo</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.cargo}
                    onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Teléfono</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.telefono}
                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
                <Form.Group className="col-md-6 mb-3 d-flex align-items-end">
                  <Form.Check
                    type="checkbox"
                    id="devenga-vacaciones"
                    label="Devenga vacaciones"
                    checked={formData.devenga_vacaciones}
                    onChange={(e) => setFormData({ ...formData, devenga_vacaciones: e.target.checked })}
                  />
                </Form.Group>
              </div>
              <div className="text-end">
                <Button variant="primary" onClick={handleSaveEmpleado}>
                  Guardar datos
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {activeTab === "vacaciones" && (
        <div className={styles.tabPage}>
          {!empleadoSeleccionado ? (
            <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
              Elegí un empleado en el selector de arriba.
            </div>
          ) : (
            <div className={styles.tabPageCard}>
              <h5 className="mb-3">
                Vacaciones — {empleadoSeleccionado.apellido} {empleadoSeleccionado.nombre}
              </h5>
              <div className="mb-4">
                <h5>Agregar Período</h5>
                <div className="row">
                  <Form.Group className="col-md-4">
                    <Form.Label>Inicio</Form.Label>
                    <Form.Control
                      type="date"
                      value={vacacionForm.fecha_inicio}
                      onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_inicio: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col-md-4">
                    <Form.Label>Fin</Form.Label>
                    <Form.Control
                      type="date"
                      value={vacacionForm.fecha_fin}
                      onChange={(e) => setVacacionForm({ ...vacacionForm, fecha_fin: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col-md-3">
                    <Form.Label>Notas</Form.Label>
                    <Form.Control
                      value={vacacionForm.notas}
                      onChange={(e) => setVacacionForm({ ...vacacionForm, notas: e.target.value })}
                    />
                  </Form.Group>
                  <div className="col-md-1 d-flex align-items-end">
                    <Button variant="success" onClick={handleAddVacacion}>
                      +
                    </Button>
                  </div>
                </div>
              </div>

              <h5>Historial</h5>
              <Table striped>
                <thead>
                  <tr>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Días</th>
                    <th>Notas</th>
                  </tr>
                </thead>
                <tbody>
                  {vacaciones.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="text-center">
                        Sin registros
                      </td>
                    </tr>
                  ) : (
                    vacaciones.map((v) => {
                      const dias =
                        Math.ceil((new Date(v.fecha_fin) - new Date(v.fecha_inicio)) / (1000 * 60 * 60 * 24)) + 1;
                      return (
                        <tr key={v.id}>
                          <td>{formatFecha(v.fecha_inicio)}</td>
                          <td>{formatFecha(v.fecha_fin)}</td>
                          <td>{dias}</td>
                          <td>{v.notas || "-"}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>

              <div className="mt-3 text-end">
                <strong>Disponibles: {calcularVacaciones(empleadoSeleccionado).disponibles} días</strong>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pestaña Haberes — bono one-off / comisión del mes (haberes_empleado). Las filas
          origen='manual' se agregan/borran acá; las 'motor_comision' (3.2) son read-only. */}
      {activeTab === "haberes" && (
        <div className={styles.tabPage}>
          {!empleadoSeleccionado ? (
            <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
              Elegí un empleado en el selector de arriba.
            </div>
          ) : (
            <div className={styles.tabPageCard}>
              <h5 className="mb-3">
                Haberes — {empleadoSeleccionado.apellido} {empleadoSeleccionado.nombre}
              </h5>
              <div className="mb-4">
                <h5>Agregar haber</h5>
                <div className="row g-2 align-items-end">
                  <Form.Group className="col-md-2">
                    <Form.Label>Mes</Form.Label>
                    <Form.Select
                      value={haberPeriodo.mes}
                      onChange={(e) => setHaberPeriodo((p) => ({ ...p, mes: Number(e.target.value) }))}
                    >
                      {MESES.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-2">
                    <Form.Label>Año</Form.Label>
                    <Form.Select
                      value={haberPeriodo.ano}
                      onChange={(e) => setHaberPeriodo((p) => ({ ...p, ano: Number(e.target.value) }))}
                    >
                      {[2024, 2025, 2026, 2027].map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-2">
                    <Form.Label>Tipo</Form.Label>
                    <Form.Select
                      value={haberForm.tipo}
                      onChange={(e) => setHaberForm((f) => ({ ...f, tipo: e.target.value }))}
                    >
                      <option value="bono">Bono</option>
                      <option value="comision">Comisión</option>
                      <option value="otro">Otro</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col-md-2">
                    <Form.Label>Monto</Form.Label>
                    <Form.Control
                      type="text"
                      inputMode="numeric"
                      value={
                        haberMontoFocus
                          ? haberForm.monto
                          : haberForm.monto === ""
                            ? ""
                            : formatMonto(Number(haberForm.monto))
                      }
                      onFocus={() => setHaberMontoFocus(true)}
                      onBlur={() => setHaberMontoFocus(false)}
                      onChange={(e) => setHaberForm((f) => ({ ...f, monto: e.target.value.replace(/\D/g, "") }))}
                    />
                  </Form.Group>
                  <Form.Group className="col-md-3">
                    <Form.Label>Concepto</Form.Label>
                    <Form.Control
                      value={haberForm.concepto}
                      onChange={(e) => setHaberForm((f) => ({ ...f, concepto: e.target.value }))}
                    />
                  </Form.Group>
                  <div className="col-md-1 d-flex align-items-end">
                    <Button variant="success" onClick={handleAddHaber}>
                      +
                    </Button>
                  </div>
                </div>
                <div className="mt-2">
                  <Form.Check
                    type="checkbox"
                    id="haber-imponible"
                    label="Imponible (entra al imponible y a la gratificación)"
                    checked={haberForm.imponible}
                    onChange={(e) => setHaberForm((f) => ({ ...f, imponible: e.target.checked }))}
                  />
                </div>
              </div>

              <h5>Haberes cargados</h5>
              <Table striped>
                <thead>
                  <tr>
                    <th>Período</th>
                    <th>Tipo</th>
                    <th>Concepto</th>
                    <th className="text-end">Monto</th>
                    <th className="text-center">Imponible</th>
                    <th>Origen</th>
                    <th className="text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const filas = (haberes || [])
                      .filter((h) => empleadoSeleccionado && h.empleado_id === empleadoSeleccionado.id)
                      .sort((a, b) => b.periodo_año - a.periodo_año || b.periodo_mes - a.periodo_mes);
                    if (filas.length === 0) {
                      return (
                        <tr>
                          <td colSpan="7" className="text-center">
                            Sin haberes cargados
                          </td>
                        </tr>
                      );
                    }
                    return filas.map((h) => (
                      <tr key={h.id}>
                        <td>{`${MESES[h.periodo_mes - 1]} ${h.periodo_año}`}</td>
                        <td className="text-capitalize">{h.tipo}</td>
                        <td>{h.concepto || "-"}</td>
                        <td className="text-end">{formatMonto(Number(h.monto))}</td>
                        <td className="text-center">{h.imponible ? "Sí" : "No"}</td>
                        <td>{h.origen === "motor_comision" ? "Motor" : "Manual"}</td>
                        <td className="text-center">
                          {h.origen === "manual" ? (
                            <Button variant="outline-danger" size="sm" onClick={() => handleDeleteHaber(h.id)}>
                              Eliminar
                            </Button>
                          ) : (
                            <span className="text-muted">—</span>
                          )}
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Remuneración (vigencias) — segunda pieza de la pestaña Ficha. */}
      {activeTab === "ficha" && empleadoSeleccionado && (
        <div className={styles.tabPage}>
          <div className={styles.tabPageCard}>
            <div className="mb-4 p-3 border rounded">
              <h5 className="mb-3">{remuneracionEditando ? "Editar" : "Agregar"} Remuneración</h5>
              <Form>
                <div className="row">
                  <Form.Group className="col mb-3">
                    <Form.Label>Inicio Vigencia</Form.Label>
                    <Form.Control
                      type="date"
                      required
                      value={remuneracionForm.inicio_vigencia}
                      onChange={(e) => setRemuneracionForm({ ...remuneracionForm, inicio_vigencia: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Sueldo Base</Form.Label>
                    {renderMontoInput("sueldo_base")}
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Comisión %</Form.Label>
                    <Form.Control
                      type="number"
                      value={remuneracionForm.comision_porcentaje}
                      onChange={(e) =>
                        setRemuneracionForm({ ...remuneracionForm, comision_porcentaje: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Bonos Fijo</Form.Label>
                    {renderMontoInput("bonos_fijo")}
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Bonos Variable</Form.Label>
                    {renderMontoInput("bonos_variable")}
                  </Form.Group>
                </div>

                <hr className="my-2" />
                <div className="text-muted mb-2" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                  PREVISIONAL (Chile)
                </div>
                <div className="row">
                  <Form.Group className="col mb-3">
                    <Form.Label>AFP</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Provida, Modelo…"
                      value={remuneracionForm.afp_nombre}
                      onChange={(e) => setRemuneracionForm({ ...remuneracionForm, afp_nombre: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>AFP cap. individual %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={remuneracionForm.afp_cap_individual_pct}
                      onChange={(e) =>
                        setRemuneracionForm({ ...remuneracionForm, afp_cap_individual_pct: e.target.value })
                      }
                    />
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>AFP comisión %</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={remuneracionForm.afp_comision_pct}
                      onChange={(e) => setRemuneracionForm({ ...remuneracionForm, afp_comision_pct: e.target.value })}
                    />
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Salud</Form.Label>
                    <Form.Select
                      value={remuneracionForm.salud_tipo}
                      onChange={(e) => setRemuneracionForm({ ...remuneracionForm, salud_tipo: e.target.value })}
                    >
                      <option value="">—</option>
                      <option value="fonasa">Fonasa</option>
                      <option value="isapre">Isapre</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="col mb-3">
                    <Form.Label>Isapre UF</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.0001"
                      disabled={remuneracionForm.salud_tipo !== "isapre"}
                      value={remuneracionForm.salud_isapre_uf}
                      onChange={(e) => setRemuneracionForm({ ...remuneracionForm, salud_isapre_uf: e.target.value })}
                    />
                  </Form.Group>
                </div>

                <div className="d-flex justify-content-end gap-2">
                  {remuneracionEditando && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => {
                        setRemuneracionEditando(null);
                        setMontoFocus(null);
                        setRemuneracionForm(REM_FORM_VACIO);
                      }}
                    >
                      Cancelar edición
                    </Button>
                  )}
                  <Button variant="primary" onClick={handleSaveRemuneracion}>
                    {remuneracionEditando ? "Actualizar" : "Agregar"}
                  </Button>
                </div>
              </Form>
            </div>

            <h5 className="mb-3">Historial de Remuneraciones</h5>
            <table className="table table-bordered table-hover align-middle">
              <thead className="table-dark text-center align-middle">
                <tr>
                  <th>INICIO VIGENCIA</th>
                  <th>FIN VIGENCIA</th>
                  <th>SUELDO BASE</th>
                  <th>COMISIÓN %</th>
                  <th>BONOS FIJO</th>
                  <th>BONOS VARIABLE</th>
                  <th>AFP</th>
                  <th>SALUD</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody>
                {remuneraciones.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center align-middle">
                      Sin registros de remuneraciones
                    </td>
                  </tr>
                ) : (
                  remuneraciones.map((rem) => (
                    <tr key={rem.id}>
                      <td className="text-center align-middle">{formatFecha(rem.inicio_vigencia)}</td>
                      <td className="text-center align-middle">
                        {rem.fin_vigencia ? formatFecha(rem.fin_vigencia) : "vigente"}
                      </td>
                      <td className="text-end align-middle">{formatAmount(rem.sueldo_base, monedaPais)}</td>
                      <td className="text-end align-middle">{rem.comision_porcentaje || 0}%</td>
                      <td className="text-end align-middle">{formatAmount(rem.bonos_fijo, monedaPais)}</td>
                      <td className="text-end align-middle">{formatAmount(rem.bonos_variable, monedaPais)}</td>
                      <td className="text-center align-middle">
                        {rem.afp_nombre
                          ? `${rem.afp_nombre} ${rem.afp_cap_individual_pct ?? "-"}%+${rem.afp_comision_pct ?? 0}%`
                          : "-"}
                      </td>
                      <td className="text-center align-middle">
                        {rem.salud_tipo === "isapre"
                          ? `Isapre ${rem.salud_isapre_uf ?? "-"} UF`
                          : rem.salud_tipo === "fonasa"
                            ? "Fonasa"
                            : "-"}
                      </td>
                      <td className="text-center align-middle">
                        <Button size="sm" variant="outline-secondary" onClick={() => handleEditRemuneracion(rem)}>
                          Editar
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Egreso — tercera pieza de la pestaña Ficha. */}
      {activeTab === "ficha" && empleadoSeleccionado && (
        <div className={styles.tabPage}>
          <div className={styles.tabPageCard}>
            <h5 className="mb-3">Egreso</h5>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Fecha de Pago</Form.Label>
                <Form.Control
                  type="date"
                  value={egresoForm.egreso_fecha}
                  onChange={(e) => setEgresoForm({ ...egresoForm, egreso_fecha: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Monto {pais === "Chile" ? "(CLP)" : "(PEN)"}</Form.Label>
                <Form.Control
                  type="number"
                  value={egresoForm.egreso_monto}
                  onChange={(e) => setEgresoForm({ ...egresoForm, egreso_monto: e.target.value })}
                />
              </Form.Group>
              <div className="text-end">
                <Button variant="primary" onClick={handleSaveEgreso}>
                  Guardar egreso
                </Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* Modal Liquidación (RETIRADA del flujo — Fase 1, 2026-10-01: el código queda intacto,
          sin ningún punto de acceso en la UI; ver docs/modulos/empleados.md). */}
      <Modal show={showLiquidacion} onHide={() => setShowLiquidacion(false)} size="lg">
        <Modal.Header closeButton style={{ background: "#2c3e50", color: "#fff" }}>
          <Modal.Title style={{ fontSize: "16px" }}>
            Liquidación — {liquidacionEmpleado?.nombre} {liquidacionEmpleado?.apellido}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {liquidacionEmpleado &&
            (() => {
              const rem = remVigenteDe(liquidacionEmpleado, liquidacionAno, liquidacionMes);
              // Selectores de mes/año: SIEMPRE visibles (aunque el mes no tenga vigencia) para poder
              // cambiar de período sin quedar trabado. El desglose se muestra solo si hay vigencia.
              const selectorMesAño = (
                <div style={{ display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }}>
                  <div>
                    <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                      Mes
                    </label>
                    <Form.Select
                      size="sm"
                      value={liquidacionMes}
                      onChange={(e) => handleLiquidacionMesChange(Number(e.target.value), liquidacionAno)}
                      style={{ width: "130px" }}
                    >
                      {MESES.map((m, i) => (
                        <option key={i} value={i + 1}>
                          {m}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                  <div>
                    <label style={{ fontSize: "12px", color: "#64748b", display: "block", marginBottom: "4px" }}>
                      Año
                    </label>
                    <Form.Select
                      size="sm"
                      value={liquidacionAno}
                      onChange={(e) => handleLiquidacionMesChange(liquidacionMes, Number(e.target.value))}
                      style={{ width: "90px" }}
                    >
                      {[2024, 2025, 2026, 2027].map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </Form.Select>
                  </div>
                </div>
              );
              // Mes sin vigencia (ej. anterior a la primera vigencia del empleado) → no se liquida:
              // espejo del `continue` de la caja. Sin desglose, pero con los selectores para navegar.
              if (!rem)
                return (
                  <>
                    {selectorMesAño}
                    <p style={{ color: "#e74c3c", margin: 0 }}>
                      Sin remuneración vigente para {MESES[liquidacionMes - 1]} {liquidacionAno}
                    </p>
                  </>
                );
              // UF/UTM del último día del mes liquidado (convención de los recibos). liquidacionMes es
              // 1-based → new Date(año, liquidacionMes, 0) = día 0 del mes siguiente = último del mes.
              const fechaTasasMes = new Date(liquidacionAno, liquidacionMes, 0).toISOString().split("T")[0];
              const ufDelMes = getUFValue(fechaTasasMes);
              const utmDelMes = getUTMValue(fechaTasasMes);
              // Adelantos NO se cargan acá (vienen de caja) → descuentosVarios = 0. Haberes
              // imponibles del mes/año elegido (bono one-off / comisión) → haberesMes.
              const liq = calcularLiquidacionChile(
                rem,
                ufDelMes,
                0,
                utmDelMes,
                haberesDelMes(liquidacionEmpleado.id, liquidacionAno, liquidacionMes)
              );
              const fmt = (n) =>
                new Intl.NumberFormat("es-CL", {
                  style: "currency",
                  currency: "CLP",
                  maximumFractionDigits: 0,
                }).format(n);
              // Pagos reales del mes/año elegido (del state ya cargado, sin queries).
              const pagosDelMes = pagosEmpleados
                .filter(
                  (p) =>
                    p.empleado_id === liquidacionEmpleado.id &&
                    p.periodo_año === liquidacionAno &&
                    p.periodo_mes === liquidacionMes
                )
                .sort((a, b) => new Date(a.fecha_pago) - new Date(b.fecha_pago));
              // Pagado a cuenta del sueldo = sueldo + adelanto del período (sumarPagadoLiquido),
              // NO todos los tipos: comisión/bono quedan en la lista pero no descuentan el líquido.
              // Así A pagar = líquido − pagadoACuenta cierra exacto (mismo util que la tabla NETO).
              const pagadoACuenta = sumarPagadoLiquido(
                pagosEmpleados,
                liquidacionEmpleado.id,
                liquidacionAno,
                liquidacionMes
              );
              const aPagar = Math.max(0, liq.liquido - pagadoACuenta);
              const filas = [
                ["Sueldo base", liq.sueldoBase, false],
                rem.bonos_fijo > 0 ? ["Bono fijo", liq.bonosFijo, false] : null,
                rem.bonos_variable > 0 ? ["Bono variable", liq.bonosVariable, false] : null,
                ["Gratificación", liq.gratificacion, false],
                ["Total imponible", liq.totalImponible, true],
                [
                  `AFP ${rem.afp_nombre || ""} (cap. ${rem.afp_cap_individual_pct ?? 10}% + com. ${rem.afp_comision_pct || 0}%)`,
                  -liq.totalAfp,
                  false,
                ],
                [
                  rem.salud_tipo === "isapre"
                    ? `Isapre (UF ${rem.salud_isapre_uf} × ${ufDelMes.toFixed(2)}, piso 7%)`
                    : "Fonasa (7%)",
                  -liq.totalSalud,
                  false,
                ],
                ["Seg. cesantía (0,6%)", -liq.segCesantia, false],
                ["Base impuesto único", liq.baseImpuesto, false],
                ["Impuesto único", -liq.impuestoUnico, false],
                ["Total descuentos legales", -liq.totalDescuentosLegales, true],
              ].filter(Boolean);
              return (
                <>
                  {selectorMesAño}

                  <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse", marginBottom: "16px" }}>
                    <tbody>
                      {filas.map(([label, valor, bold], i) => (
                        <tr
                          key={i}
                          style={{
                            borderBottom: "1px solid #f1f5f9",
                            background: bold ? "#f8fafc" : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "7px 8px",
                              color: "#475569",
                              fontWeight: bold ? 500 : 400,
                              textAlign: "left",
                            }}
                          >
                            {label}
                          </td>
                          <td
                            style={{
                              padding: "7px 8px",
                              textAlign: "right",
                              fontWeight: bold ? 500 : 400,
                              color: valor < 0 ? "#e74c3c" : "#1e293b",
                            }}
                          >
                            {fmt(Math.abs(valor))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Bloque del sueldo (tono suave): Líquido del mes − Adelantos = A pagar. */}
                  <div
                    style={{
                      background: "#f1f5f9",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                      padding: "12px 16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "6px",
                      }}
                    >
                      <span>Líquido del mes</span>
                      <span>{fmt(liq.liquido)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        fontSize: "13px",
                        color: "#64748b",
                        marginBottom: "8px",
                      }}
                    >
                      <span>Adelantos</span>
                      <span>{pagadoACuenta > 0 ? `− ${fmt(pagadoACuenta)}` : fmt(0)}</span>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderTop: "1px solid #cbd5e1",
                        paddingTop: "8px",
                      }}
                    >
                      <span style={{ fontSize: "14px", fontWeight: 600, color: "#1e293b" }}>A pagar</span>
                      <span style={{ fontSize: "20px", fontWeight: 600, color: "#1e293b" }}>{fmt(aPagar)}</span>
                    </div>
                  </div>

                  {/* Pagos extraordinarios (transporte, comisión, bono, etc.), separados del sueldo. */}
                  <div style={{ marginTop: "20px", borderTop: "1px solid #e2e8f0", paddingTop: "16px" }}>
                    <div style={{ fontSize: "12px", color: "#64748b", fontWeight: 600, marginBottom: "6px" }}>
                      Pagos del mes
                    </div>
                    {pagosDelMes.length === 0 ? (
                      <div style={{ fontSize: "13px", color: "#94a3b8" }}>Sin pagos registrados este mes</div>
                    ) : (
                      <table style={{ width: "100%", fontSize: "13px", borderCollapse: "collapse" }}>
                        <tbody>
                          {pagosDelMes.map((p) => (
                            <tr key={p.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                              <td style={{ padding: "6px 8px", color: "#475569" }}>{formatFecha(p.fecha_pago)}</td>
                              <td style={{ padding: "6px 8px", color: "#475569", textTransform: "capitalize" }}>
                                {p.tipo}
                              </td>
                              <td style={{ padding: "6px 8px", textAlign: "right", color: "#1e293b" }}>
                                {fmt(Number(p.monto || 0))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                </>
              );
            })()}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowLiquidacion(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Pestaña Liquidaciones (PDF legal de la contadora, PASO C2 — rediseño 2026-09-29, pasada a
          página completa en la Fase 1 de pestañas 2026-10-01). Mismo estilo que la tabla de Detalle
          de Ventas (stylesVentasDetalle.scss, importado arriba — cero estilos inventados, 10.161).
          Ver docs/decisiones/2026-09-29-liquidaciones-recibo-ventasdetalle-estilo.md. */}
      {activeTab === "liquidaciones" && (
        <div className={styles.tabPage}>
          {!liquidacionesEmpleado ? (
            <div className="text-center" style={{ padding: 40, color: "#64748b" }}>
              Elegí un empleado en el selector de arriba.
            </div>
          ) : (
            <div className={styles.tabPageCard}>
              <h5 className="mb-3">
                Liquidaciones — {liquidacionesEmpleado.nombre} {liquidacionesEmpleado.apellido}
              </h5>
              {(() => {
                const diffColor = (v) => (v != null && Math.abs(v) > 0 ? "#dc3545" : "#1e293b");
                const analizarActivo = esAdminLiquidaciones && analizarLiquidaciones;
                const datosPorMes = MESES.map((_, i) => datosDelMes(liquidacionesEmpleado, liquidacionesAnoSel, i + 1));
                const totalColsRecibo = 8 + (analizarActivo ? 3 : 0);

                const totalBrutoAnual = datosPorMes.reduce((s, d) => s + (d.totalBruto || 0), 0);
                const totalDescLegalesAnual = datosPorMes.reduce((s, d) => s + (d.descLegales?.total || 0), 0);
                const totalOtrosDescAnual = datosPorMes.reduce((s, d) => s + (d.totalOtrosDesc || 0), 0);
                const totalLiquidoAnual = datosPorMes.reduce((s, d) => s + (d.liquido || 0), 0);
                const totalPagadoAnual = datosPorMes.reduce((s, d) => s + (d.pagadoTotal || 0), 0);
                const totalSaldoAnual = totalLiquidoAnual - totalPagadoAnual;
                const totalPlataformaAnual = datosPorMes.reduce((s, d) => s + (d.plataforma?.liquido || 0), 0);

                // Recibo del mes: haberes (art. 54 Código del Trabajo) → descuentos legales → otros
                // descuentos → líquido → pagado → saldo. Función local (no un componente aparte): solo
                // la usa este modal, misma convención de IIFEs que el resto del archivo.
                const renderRecibo = (d, mes) => {
                  const sumaComision = d.comisionesDelMes.reduce((s, h) => s + (Number(h.monto) || 0), 0);
                  const detalleKey = `${liquidacionesAnoSel}-${mes}`;
                  const detalleResuelto = detalleComisionCache[detalleKey];
                  return (
                    <div className="details-container" style={{ padding: "12px 20px" }}>
                      {d.esEstimado && (
                        <p style={{ color: "#999", fontSize: "0.85em" }}>
                          Sin liquidación de la contadora cargada para este mes — valores <strong>estimados</strong> por
                          la plataforma.
                        </p>
                      )}

                      <h6>Haberes imponibles</h6>
                      <table className="table table-bordered table-striped table-hover table-sm">
                        <thead className="text-center align-middle">
                          <tr>
                            <th className="text-start">Concepto</th>
                            <th className="text-end">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.haberesLineas.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="text-center align-middle">
                                Sin datos
                              </td>
                            </tr>
                          ) : (
                            d.haberesLineas.map((h, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <tr key={idx}>
                                <td className="text-start align-middle">{h.etiqueta}</td>
                                <td className="text-end align-middle">{formatMonto(h.monto)}</td>
                              </tr>
                            ))
                          )}
                          <tr style={{ fontWeight: 700 }}>
                            <td className="text-start align-middle">Total bruto</td>
                            <td className="text-end align-middle">
                              {d.totalBruto != null ? formatMonto(d.totalBruto) : "-"}
                            </td>
                          </tr>
                        </tbody>
                      </table>

                      {d.comisionesDelMes.length > 0 && (
                        <div className="mb-3">
                          <strong
                            className="collapsible-title"
                            onClick={() => toggleComisionRecibo(liquidacionesAnoSel, mes, d.comisionesDelMes)}
                          >
                            {comisionAbierta ? "▾" : "▸"} Comisión — de qué ventas sale ({formatMonto(sumaComision)})
                          </strong>
                          {comisionAbierta && (
                            <div className="collapse show">
                              {cargandoComision && !detalleResuelto ? (
                                <p style={{ fontSize: "0.85em", color: "#999" }}>Resolviendo ventas…</p>
                              ) : (
                                <table className="table table-bordered table-striped table-hover table-sm mt-2">
                                  <thead className="text-center align-middle">
                                    <tr>
                                      <th>Cliente</th>
                                      <th>Factura</th>
                                      <th>Fecha de cobro</th>
                                      <th>Concepto</th>
                                      <th className="text-end">Comisión</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {(detalleResuelto || d.comisionesDelMes).map((h, idx) => (
                                      // eslint-disable-next-line react/no-array-index-key
                                      <tr key={idx}>
                                        <td className="text-start align-middle">{h.cliente || "-"}</td>
                                        <td className="text-center align-middle">{h.factura || "-"}</td>
                                        <td className="text-center align-middle">{h.fechaCobro || "-"}</td>
                                        <td className="text-start align-middle">{h.concepto || "-"}</td>
                                        <td className="text-end align-middle">{formatMonto(h.monto)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          )}
                        </div>
                      )}

                      <h6>Descuentos legales</h6>
                      <table className="table table-bordered table-striped table-hover table-sm">
                        <thead className="text-center align-middle">
                          <tr>
                            <th className="text-start">Concepto</th>
                            <th className="text-end">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.descLegales ? (
                            <>
                              <tr>
                                <td className="text-start align-middle">AFP</td>
                                <td className="text-end align-middle">{formatMonto(d.descLegales.afp)}</td>
                              </tr>
                              <tr>
                                <td className="text-start align-middle">Salud</td>
                                <td className="text-end align-middle">{formatMonto(d.descLegales.salud)}</td>
                              </tr>
                              <tr>
                                <td className="text-start align-middle">Seguro de cesantía</td>
                                <td className="text-end align-middle">{formatMonto(d.descLegales.cesantia)}</td>
                              </tr>
                              <tr>
                                <td className="text-start align-middle">Impuesto único</td>
                                <td className="text-end align-middle">{formatMonto(d.descLegales.impuesto)}</td>
                              </tr>
                              <tr style={{ fontWeight: 700 }}>
                                <td className="text-start align-middle">Total descuentos legales</td>
                                <td className="text-end align-middle">{formatMonto(d.descLegales.total)}</td>
                              </tr>
                            </>
                          ) : (
                            <tr>
                              <td colSpan={2} className="text-center align-middle">
                                Sin datos
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      <h6>Otros descuentos (anticipos, préstamos)</h6>
                      <table className="table table-bordered table-striped table-hover table-sm">
                        <thead className="text-center align-middle">
                          <tr>
                            <th className="text-start">Concepto</th>
                            <th className="text-end">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.otrosDescLineas.length === 0 ? (
                            <tr>
                              <td colSpan={2} className="text-center align-middle">
                                {d.esEstimado
                                  ? "No modelado por la plataforma (estimado)"
                                  : "Sin otros descuentos este mes"}
                              </td>
                            </tr>
                          ) : (
                            d.otrosDescLineas.map((h, idx) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <tr key={idx}>
                                <td className="text-start align-middle">{h.etiqueta}</td>
                                <td className="text-end align-middle">{formatMonto(h.monto)}</td>
                              </tr>
                            ))
                          )}
                          <tr style={{ fontWeight: 700 }}>
                            <td className="text-start align-middle">Total otros descuentos</td>
                            <td className="text-end align-middle">{formatMonto(d.totalOtrosDesc)}</td>
                          </tr>
                        </tbody>
                      </table>

                      <p style={{ fontSize: "1.05em", fontWeight: 700, color: "#343a40" }}>
                        LÍQUIDO A PAGAR: {d.liquido != null ? formatMonto(d.liquido) : "-"}
                      </p>

                      <h6>Pagado</h6>
                      <table className="table table-bordered table-striped table-hover table-sm">
                        <thead className="text-center align-middle">
                          <tr>
                            <th>Fecha</th>
                            <th>Tipo</th>
                            <th className="text-end">Monto</th>
                          </tr>
                        </thead>
                        <tbody>
                          {d.pagosReales.length === 0 ? (
                            <tr>
                              <td colSpan={3} className="text-center align-middle">
                                Sin pagos con transferencia este mes
                              </td>
                            </tr>
                          ) : (
                            d.pagosReales.map((p) => (
                              <tr key={p.id}>
                                <td className="text-center align-middle">{formatFecha(p.fecha_pago)}</td>
                                <td className="text-center align-middle text-capitalize">{p.tipo}</td>
                                <td className="text-end align-middle">{formatMonto(p.monto)}</td>
                              </tr>
                            ))
                          )}
                          <tr style={{ fontWeight: 700 }}>
                            <td className="text-start align-middle" colSpan={2}>
                              Total pagado
                            </td>
                            <td className="text-end align-middle">{formatMonto(d.pagadoTotal)}</td>
                          </tr>
                        </tbody>
                      </table>

                      {d.pagosSinTransferencia.length > 0 && (
                        <p style={{ color: "#999", fontSize: "0.85em" }}>
                          Sin transferencia (no suman al pagado):{" "}
                          {d.pagosSinTransferencia
                            .map((p) => `${p.tipo} ${formatMonto(p.monto)}${p.concepto ? ` (${p.concepto})` : ""}`)
                            .join(" · ")}
                        </p>
                      )}

                      <p
                        style={{
                          fontSize: "1.05em",
                          fontWeight: 700,
                          color: d.saldo != null && Math.abs(d.saldo) > 0 ? "#dc3545" : "#343a40",
                        }}
                      >
                        SALDO: {d.saldo != null ? formatMonto(d.saldo) : "-"}
                      </p>
                    </div>
                  );
                };

                return (
                  <>
                    {esAdminLiquidaciones && (
                      <div style={{ marginBottom: "16px" }}>
                        <Form.Check
                          type="checkbox"
                          id="analizar-liquidaciones"
                          label="Analizar"
                          checked={analizarLiquidaciones}
                          onChange={(e) => setAnalizarLiquidaciones(e.target.checked)}
                          style={{ fontSize: "13px" }}
                        />
                      </div>
                    )}

                    {liquidacionesCargandoLista ? (
                      <Loader />
                    ) : (
                      <>
                        <div className="container-ventas-detalle" style={{ minHeight: 0, margin: 0, padding: 0 }}>
                          <div className="row justify-content-center">
                            <div className="col-md-12">
                              <table className="table table-bordered table-striped table-hover table-sm">
                                <thead className="text-center align-middle">
                                  <tr>
                                    <th style={{ width: "10%" }}>MES</th>
                                    <th>BRUTO</th>
                                    <th>DESC. LEGALES</th>
                                    <th>OTROS DESC.</th>
                                    <th>LÍQUIDO</th>
                                    <th>PAGADO</th>
                                    <th>SALDO</th>
                                    <th style={{ width: "8%" }}>PDF</th>
                                    {analizarActivo && (
                                      <>
                                        <th>PLATAFORMA</th>
                                        <th>DIF. CONTADORA VS PLATAFORMA</th>
                                        <th>DIF. LÍQUIDO VS PAGADO</th>
                                      </>
                                    )}
                                  </tr>
                                </thead>
                                <tbody className="table-striped">
                                  {MESES.map((nombreMes, i) => {
                                    const mes = i + 1;
                                    const d = datosPorMes[i];
                                    const abierto = liquidacionesMesSel === mes && reciboAbierto;
                                    const diffContadoraPlataforma =
                                      d.contadora && d.plataforma ? d.contadora.liquido - d.plataforma.liquido : null;
                                    return (
                                      <React.Fragment key={mes}>
                                        <tr onClick={() => toggleMesRecibo(mes)} style={{ cursor: "pointer" }}>
                                          <td className="text-start align-middle">
                                            <span
                                              style={{
                                                marginRight: 4,
                                                display: "inline-block",
                                                transform: abierto ? "rotate(90deg)" : "rotate(0deg)",
                                                transition: "transform 0.2s",
                                              }}
                                            >
                                              ▶
                                            </span>
                                            {nombreMes}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.totalBruto != null ? formatMonto(d.totalBruto) : "-"}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.descLegales?.total != null ? formatMonto(d.descLegales.total) : "-"}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.totalOtrosDesc ? formatMonto(d.totalOtrosDesc) : "-"}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.liquido != null ? formatMonto(d.liquido) : "-"}
                                            {d.esEstimado && d.liquido != null && (
                                              <span style={{ color: "#999", fontSize: "0.75em" }}> (estimado)</span>
                                            )}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.pagadoTotal > 0 ? formatMonto(d.pagadoTotal) : "-"}
                                          </td>
                                          <td className="text-end align-middle">
                                            {d.saldo != null ? formatMonto(d.saldo) : "-"}
                                          </td>
                                          <td className="text-center align-middle">
                                            {d.contadora ? (
                                              <span
                                                style={{ cursor: "pointer", color: "#007bff" }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleDescargarLiquidacionContadora(d.contadora);
                                                }}
                                              >
                                                ⬇ PDF
                                              </span>
                                            ) : (
                                              <span style={{ color: "#ccc" }}>-</span>
                                            )}
                                          </td>
                                          {analizarActivo && (
                                            <>
                                              <td className="text-end align-middle">
                                                {d.plataforma ? formatMonto(d.plataforma.liquido) : "-"}
                                              </td>
                                              <td
                                                className="text-end align-middle"
                                                style={{ color: diffColor(diffContadoraPlataforma) }}
                                              >
                                                {diffContadoraPlataforma != null
                                                  ? formatMonto(diffContadoraPlataforma)
                                                  : "-"}
                                              </td>
                                              <td
                                                className="text-end align-middle"
                                                style={{ color: diffColor(d.liquido != null ? d.saldo : null) }}
                                              >
                                                {d.liquido != null ? formatMonto(d.saldo) : "-"}
                                              </td>
                                            </>
                                          )}
                                        </tr>
                                        {abierto && (
                                          <tr style={{ backgroundColor: "rgba(0,0,0,0.02)" }}>
                                            <td
                                              colSpan={totalColsRecibo}
                                              style={{ cursor: "default" }}
                                              onClick={(e) => e.stopPropagation()}
                                            >
                                              {renderRecibo(d, mes)}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    );
                                  })}
                                </tbody>
                                <tfoot>
                                  <tr style={{ fontWeight: 700 }}>
                                    <td className="text-start align-middle">Total {liquidacionesAnoSel}</td>
                                    <td className="text-end align-middle">{formatMonto(totalBrutoAnual)}</td>
                                    <td className="text-end align-middle">{formatMonto(totalDescLegalesAnual)}</td>
                                    <td className="text-end align-middle">{formatMonto(totalOtrosDescAnual)}</td>
                                    <td className="text-end align-middle">{formatMonto(totalLiquidoAnual)}</td>
                                    <td className="text-end align-middle">{formatMonto(totalPagadoAnual)}</td>
                                    <td className="text-end align-middle">{formatMonto(totalSaldoAnual)}</td>
                                    <td />
                                    {analizarActivo && (
                                      <>
                                        <td className="text-end align-middle">{formatMonto(totalPlataformaAnual)}</td>
                                        <td
                                          className="text-end align-middle"
                                          style={{ color: diffColor(totalLiquidoAnual - totalPlataformaAnual) }}
                                        >
                                          {formatMonto(totalLiquidoAnual - totalPlataformaAnual)}
                                        </td>
                                        <td
                                          className="text-end align-middle"
                                          style={{ color: diffColor(totalSaldoAnual) }}
                                        >
                                          {formatMonto(totalSaldoAnual)}
                                        </td>
                                      </>
                                    )}
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>

                        {esAdminLiquidaciones && (
                          <div className="mt-4 p-3 border rounded">
                            <h6 className="mb-2">Cargar liquidación</h6>
                            <Form.Control
                              type="file"
                              accept="application/pdf"
                              onChange={(e) => handleSeleccionArchivoLiquidacion(e.target.files?.[0] || null)}
                              disabled={procesandoArchivoLiquidacion || guardandoLiquidacionContadora}
                            />
                            {procesandoArchivoLiquidacion && (
                              <div className="mt-2 text-muted" style={{ fontSize: "13px" }}>
                                Leyendo el PDF…
                              </div>
                            )}
                            {previewLiquidacion && !previewLiquidacion.ok && (
                              <div className="mt-2 text-danger" style={{ fontSize: "13px" }}>
                                Rechazado: {previewLiquidacion.motivo}
                              </div>
                            )}
                            {previewLiquidacion?.ok && (
                              <div className="mt-3">
                                <div style={{ fontSize: "13px", marginBottom: "8px" }}>
                                  Período <strong>{previewLiquidacion.periodo}</strong> —{" "}
                                  {previewLiquidacion.empleado.nombre} {previewLiquidacion.empleado.apellido}
                                </div>
                                <Table size="sm" style={{ fontSize: "13px" }}>
                                  <tbody>
                                    <tr>
                                      <td>Total haberes</td>
                                      <td className="text-end">{formatMonto(previewLiquidacion.data.total_haberes)}</td>
                                    </tr>
                                    <tr>
                                      <td>Total descuentos legales</td>
                                      <td className="text-end">
                                        {formatMonto(previewLiquidacion.data.total_descuentos_legales)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>Total descuentos varios (anticipos)</td>
                                      <td className="text-end">
                                        {formatMonto(previewLiquidacion.data.total_descuentos_varios)}
                                      </td>
                                    </tr>
                                    <tr>
                                      <td>
                                        <strong>Líquido</strong>
                                      </td>
                                      <td className="text-end">
                                        <strong>{formatMonto(previewLiquidacion.data.liquido)}</strong>
                                      </td>
                                    </tr>
                                  </tbody>
                                </Table>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleConfirmarCargaLiquidacion}
                                  disabled={guardandoLiquidacionContadora}
                                >
                                  {guardandoLiquidacionContadora ? "Guardando…" : "Confirmar y guardar"}
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}

      {/* Modal Carga masiva de liquidaciones (botón general "Cargar liquidaciones", solo admin) —
          sigue siendo modal: es una acción puntual de carga por lote, no una vista de un empleado. */}
      <Modal show={showCargaMasiva} onHide={() => setShowCargaMasiva(false)} size="xl">
        <Modal.Header closeButton style={{ background: "#2c3e50", color: "#fff" }}>
          <Modal.Title style={{ fontSize: "16px" }}>Cargar liquidaciones</Modal.Title>
        </Modal.Header>
        <Modal.Body className={styles.modalScrollBody}>
          <Form.Group className="mb-3">
            <Form.Label>Seleccioná uno o varios PDF</Form.Label>
            <Form.Control
              key={cargaMasivaInputKey}
              type="file"
              accept="application/pdf"
              multiple
              onChange={(e) => handleSeleccionArchivosMasivos(e.target.files)}
              disabled={procesandoMasiva || guardandoMasiva}
            />
          </Form.Group>

          {procesandoMasiva && (
            <div className="text-muted" style={{ fontSize: "13px" }}>
              Leyendo y validando los PDF…
            </div>
          )}

          {filasMasivas.length > 0 && (
            <>
              <Table bordered size="sm" style={{ fontSize: "13px" }}>
                <thead>
                  <tr>
                    <th>Archivo</th>
                    <th>Empleado</th>
                    <th>Mes</th>
                    <th className="text-end">Haberes</th>
                    <th className="text-end">Desc. legales</th>
                    <th className="text-end">Desc. varios</th>
                    <th className="text-end">Líquido</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {filasMasivas.map((fila, i) => {
                    const r = fila.resultado;
                    const empleadoLabel = r.empleado
                      ? `${r.empleado.nombre} ${r.empleado.apellido}`
                      : r.rutExtraido
                        ? `RUT ${r.rutExtraido} (no identificado)`
                        : "—";
                    const colorFondo =
                      fila.estado === "rechazado" ? "#fdecea" : fila.estado === "ya_existe" ? "#fff8e1" : undefined;
                    return (
                      <tr key={`${fila.archivo}-${i}`} style={{ background: colorFondo }}>
                        <td>{fila.archivo}</td>
                        <td>{empleadoLabel}</td>
                        <td>{r.periodo || "—"}</td>
                        <td className="text-end">{r.data ? formatMonto(r.data.total_haberes) : "—"}</td>
                        <td className="text-end">{r.data ? formatMonto(r.data.total_descuentos_legales) : "—"}</td>
                        <td className="text-end">{r.data ? formatMonto(r.data.total_descuentos_varios) : "—"}</td>
                        <td className="text-end">{r.data ? formatMonto(r.data.liquido) : "—"}</td>
                        <td style={{ fontSize: "12px" }}>
                          {fila.estado === "ok" && <span style={{ color: "#2e7d32" }}>OK</span>}
                          {fila.estado === "ya_existe" && (
                            <span style={{ color: "#b8860b" }}>Ya existe — cargará versión nueva</span>
                          )}
                          {fila.estado === "rechazado" && (
                            <span style={{ color: "#c62828" }}>Rechazado: {fila.motivo}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>

              {(() => {
                const rechazadas = filasMasivas.filter((f) => f.estado === "rechazado").length;
                const listas = filasMasivas.length - rechazadas;
                return (
                  <div style={{ fontSize: "13px", marginBottom: "8px" }}>
                    {rechazadas > 0 ? (
                      <span style={{ color: "#c62828" }}>
                        {rechazadas} archivo(s) rechazado(s) — sacalos de la selección para poder confirmar.
                      </span>
                    ) : (
                      <span>{listas} archivo(s) listo(s) para cargar.</span>
                    )}
                  </div>
                );
              })()}

              {progresoMasivo && (
                <div style={{ fontSize: "13px", marginBottom: "8px" }}>
                  Guardando {progresoMasivo.hechos + 1}/{progresoMasivo.total}: {progresoMasivo.archivo}…
                </div>
              )}

              {resultadoMasivo && (
                <div
                  style={{
                    fontSize: "13px",
                    marginBottom: "8px",
                    color: resultadoMasivo.error ? "#c62828" : "#2e7d32",
                  }}
                >
                  {resultadoMasivo.error
                    ? resultadoMasivo.error
                    : `${resultadoMasivo.guardados}/${resultadoMasivo.total} liquidaciones cargadas correctamente.`}
                </div>
              )}

              <Button
                variant="primary"
                size="sm"
                onClick={handleConfirmarCargaMasiva}
                disabled={
                  guardandoMasiva || filasMasivas.length === 0 || filasMasivas.some((f) => f.estado === "rechazado")
                }
              >
                {guardandoMasiva ? "Guardando…" : "Confirmar carga"}
              </Button>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowCargaMasiva(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default GestionEmpleados;
